import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, assetUrl } from "@/lib/api";import { jmd } from "@/lib/format";
import { ShopNowAIMascot } from "@/components/ShopNowAIMascot";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, BarChart, Bar, Line, Legend } from "recharts";
import { Globe, Gauge, DollarSign, ShoppingCart, Users, Store, Hotel, LifeBuoy, Loader2, LayoutGrid, Package, UserCog, PhoneCall, Gavel, Check, X, RotateCcw, Activity, Radio, Eye, TrendingUp, AlertTriangle, Plus, ClipboardList, Search, Download, Upload, Save, EyeOff, Pencil, ChevronLeft, ChevronRight, Flag, CheckSquare, Square, Tag, Sparkles, Send, Lightbulb, Sailboat, Server, HeartPulse, ScrollText, FileText, RefreshCw, Inbox, Container, Cpu, ShieldCheck, Play, Bot, Trash2, Mail, Bell, History, Lock } from "lucide-react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { MfaPanel } from "@/components/MfaPanel";
import { AdminTravelPanel } from "@/components/AdminTravelCalc";
import { AdminPalletPanel } from "@/components/AdminPalletPanel";
import { AdminMaterialsPanel } from "@/components/AdminMaterialsPanel";
import { AdminVehiclesPanel } from "@/components/AdminVehiclesPanel";
import { AdminHaulagePanel } from "@/components/AdminHaulagePanel";
import { AdminAIGovernancePanel } from "@/components/AdminAIGovernancePanel";
import { AdminImportQueue } from "@/components/AdminImportQueue";
import { AdminPriceLog } from "@/components/AdminPriceLog";
import { RoleGovernance } from "@/components/RoleGovernance";
import { OpsDashboard } from "@/components/OpsDashboard";
import { ResiliencePanel } from "@/components/ResiliencePanel";
import { AssurancePanel } from "@/components/AssurancePanel";
import { KnowledgePanel } from "@/components/KnowledgePanel";
import { CompliancePanel } from "@/components/CompliancePanel";
import { ExportPanel } from "@/components/ExportPanel";
import { MonteCarloCenter } from "@/components/MonteCarloCenter";
import { DealApproval } from "@/components/DealApproval";
import { CatalogueAudit } from "@/components/CatalogueAudit";
import { EnterpriseDashboard } from "@/components/EnterpriseDashboard";
import { FounderAICenter } from "@/components/FounderAICenter";
import { ProviderSwitch } from "@/components/ProviderSwitch";
import { AIOpsDashboard } from "@/components/AIOpsDashboard";
import { SupplierVault } from "@/components/SupplierVault";
import { ReleasePanel } from "@/components/ReleasePanel";
import { CatalogueSync } from "@/components/CatalogueSync";
import { FounderBI } from "@/components/FounderBI";
import { AccountingLevels } from "@/components/AccountingLevels";
import { AdminMasterView, AlertCenter, DailyBriefPanel } from "@/components/AdminMasterView";
import { AdminSearchPanel } from "@/components/AdminSearch";
import { CatalogueQA } from "@/components/CatalogueQA";
import { LanguageDashboard } from "@/components/LanguageDashboard";
import { WeatherDashboard } from "@/components/WeatherDashboard";
import { CommandCenterPanel } from "@/components/CommandCenter";
import InventoryAdvisor from "@/components/InventoryAdvisor";
import { AuditorPortal } from "@/components/AuditorPortal";

const PER_PAGE = 40;

function AIStat({ label, value, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-4" data-testid={testid}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-bold mt-1 truncate">{value}</div>
    </div>
  );
}

const newMsg = (role, text) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, role, text });

function FygaroPayLinkPanel() {
  const [enabled, setEnabled] = useState(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/payments/fygaro/status").then(({ data }) => setEnabled(!!data.enabled)).catch(() => setEnabled(false)); }, []);
  const gen = async () => {
    if (!Number(amount)) { toast.error("Enter an amount"); return; }
    setBusy(true); setLink("");
    try {
      const { data } = await api.post("/admin/fygaro/pay-link", { amount: Number(amount), reference: reference || undefined, description: description || undefined, email: email || undefined });
      setLink(data.checkout_url);
      toast.success(data.emailed ? "Pay link generated & emailed to customer" : "Pay link generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate link"); }
    setBusy(false);
  };
  return (
    <div className="bg-card rounded-2xl border border-border p-6 max-w-lg space-y-3" data-testid="fygaro-paylink-panel">
      <div>
        <h3 className="font-display font-bold text-lg">Fygaro Pay Link (Invoices)</h3>
        <p className="text-sm text-muted-foreground mt-1">Generate a secure Fygaro payment link for a customer quote or invoice, and optionally email it to them.</p>
      </div>
      {enabled === false && <div className="rounded-xl bg-secondary/60 p-3 text-sm text-muted-foreground">Fygaro is not set up yet. Add your Fygaro API credentials to enable this.</div>}
      <div className={enabled === false ? "opacity-50 pointer-events-none space-y-3" : "space-y-3"}>
        <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (JMD)" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="paylink-amount" />
        <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference (e.g. Invoice #1024)" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="paylink-reference" />
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="paylink-description" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Customer email (optional — we'll send the link)" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="paylink-email" />
        <button onClick={gen} disabled={busy} className="bg-ink text-white rounded-xl px-5 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="paylink-generate-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <DollarSign size={15} />} Generate pay link
        </button>
        {link && (
          <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-2" data-testid="paylink-result">
            <div className="flex items-center gap-3">
              <QRCodeCanvas value={link} size={80} />
              <div className="min-w-0">
                <a href={link} target="_blank" rel="noreferrer" className="text-emerald font-semibold text-sm break-all">{link}</a>
                <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }} className="ml-2 text-xs underline" data-testid="paylink-copy-btn">Copy</button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Scan to pay, or share the link on WhatsApp.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const CURRENCIES = ["JMD", "USD", "CAD", "GBP"];
function PayoutsSecurityPanel() {
  const [acc, setAcc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [fx, setFx] = useState(null);
  const [fxBusy, setFxBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/payout-accounts").then(({ data }) => setAcc(data)).catch(() => {});
    api.get("/admin/fx-rates").then(({ data }) => setFx(data)).catch(() => {});
  }, []);
  const refreshFx = async () => {
    setFxBusy(true);
    try { await api.post("/admin/fx-rates/refresh"); const { data } = await api.get("/admin/fx-rates"); setFx(data); toast.success("Exchange rates updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not refresh rates"); }
    setFxBusy(false);
  };
  const setField = (k, v) => setAcc((a) => ({ ...a, [k]: v }));
  const setAccount = (cur, v) => setAcc((a) => ({ ...a, accounts: { ...a.accounts, [cur]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/payout-accounts", {
        bank_name: acc.bank_name, branch: acc.branch, branch_address: acc.branch_address || "",
        swift: acc.swift, transit: acc.transit || "", branch_code: acc.branch_code || "",
        manager: acc.manager || "", contact_email: acc.contact_email || "", accounts: acc.accounts || {},
      });
      setAcc(data); toast.success("Payout accounts updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };
  const grantAdmin = async () => {
    if (!grantEmail.trim()) { toast.error("Enter an email"); return; }
    try {
      await api.post("/admin/users/grant-admin", { email: grantEmail.trim() });
      toast.success(`${grantEmail.trim()} is now an admin`); setGrantEmail("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not grant admin"); }
  };
  if (!acc) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={26} /></div>;
  return (
    <div className="space-y-6 max-w-2xl" data-testid="payouts-panel">
      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><DollarSign size={18} className="text-emerald" /> Bank Transfer Payout Accounts</h3>
        <p className="text-xs text-muted-foreground mb-4">The account a shopper is shown at checkout is chosen automatically by their delivery country's currency. Editing these is a protected action and asks you to confirm your identity.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-muted-foreground">Bank name<input value={acc.bank_name || ""} onChange={(e) => setField("bank_name", e.target.value)} className="w-full rounded-xl border border-border px-3 py-2.5 text-sm mt-1 text-ink font-normal" data-testid="payout-bank-name" /></label>
          <label className="text-xs font-semibold text-muted-foreground">Branch<input value={acc.branch || ""} onChange={(e) => setField("branch", e.target.value)} className="w-full rounded-xl border border-border px-3 py-2.5 text-sm mt-1 text-ink font-normal" /></label>
          <label className="text-xs font-semibold text-muted-foreground">SWIFT<input value={acc.swift || ""} onChange={(e) => setField("swift", e.target.value)} className="w-full rounded-xl border border-border px-3 py-2.5 text-sm mt-1 text-ink font-normal" data-testid="payout-swift" /></label>
          <label className="text-xs font-semibold text-muted-foreground">Transit / Branch code<div className="flex gap-2 mt-1"><input value={acc.transit || ""} onChange={(e) => setField("transit", e.target.value)} className="w-full rounded-xl border border-border px-3 py-2.5 text-sm text-ink font-normal" /><input value={acc.branch_code || ""} onChange={(e) => setField("branch_code", e.target.value)} className="w-full rounded-xl border border-border px-3 py-2.5 text-sm text-ink font-normal" /></div></label>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {CURRENCIES.map((cur) => (
            <label key={cur} className="text-xs font-semibold text-muted-foreground">{cur} account #
              <input value={acc.accounts?.[cur] || ""} onChange={(e) => setAccount(cur, e.target.value)} className="w-full rounded-xl border border-border px-3 py-2.5 text-sm mt-1 text-ink font-normal font-mono-sn" data-testid={`payout-acct-${cur}`} />
            </label>
          ))}
        </div>
        <button onClick={save} disabled={saving} className="mt-4 bg-ink text-white rounded-xl px-6 py-3 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="payout-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save payout accounts
        </button>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><RefreshCw size={18} className="text-emerald" /> Live Exchange Rates</h3>
          <button onClick={refreshFx} disabled={fxBusy} className="text-xs font-semibold border border-border rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-60" data-testid="fx-refresh-btn">{fxBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh now</button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Bank-transfer amounts convert from J$ using these rates, auto-refreshed daily.{fx?.updated_at ? ` Last updated ${new Date(fx.updated_at).toLocaleString()}.` : " Using default rates until first refresh."}</p>
        <div className="grid grid-cols-3 gap-3">
          {["USD", "CAD", "GBP"].map((c) => (
            <div key={c} className="rounded-xl border border-border p-3" data-testid={`fx-${c}`}>
              <div className="text-xs text-muted-foreground">J$1 = {c}</div>
              <div className="font-display font-bold text-lg">{fx?.rates?.[c] ? Number(fx.rates[c]).toFixed(5) : "—"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><ShieldCheck size={18} className="text-emerald" /> Grant Admin Access</h3>
        <p className="text-xs text-muted-foreground mb-4">Promote an existing user to admin. Protected action — you'll confirm your identity.</p>
        <div className="flex gap-2">
          <input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="user@email.com" className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="grant-admin-email" />
          <button onClick={grantAdmin} className="bg-coral text-white rounded-xl px-5 py-2.5 font-semibold text-sm" data-testid="grant-admin-btn">Make admin</button>
        </div>
      </div>
    </div>
  );
}


function CartRecoveryPanel() {
  const [d, setD] = useState(null);
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    api.get("/admin/cart-recovery").then(({ data }) => setD(data)).catch(() => {});
    api.get("/admin/cart-recovery/trends").then(({ data }) => setTrend(data.days || [])).catch(() => {});
  }, []);
  if (!d) return null;
  const Stat = ({ label, value, testid, cls }) => (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className={`text-2xl font-display font-bold ${cls || "text-ink"}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
  const chartData = trend.map((x) => ({ ...x, label: x.date.slice(5) }));
  return (
    <div className="mt-6 space-y-4 max-w-2xl" data-testid="cart-recovery-panel">
      <div>
        <h3 className="font-display font-bold text-lg">Cart Recovery</h3>
        <p className="text-sm text-muted-foreground mt-1">How win-back nudges are performing.</p>
      </div>
      <div className="rounded-2xl bg-ink text-white p-5 flex items-center justify-between" data-testid="cr-revenue">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-emerald font-semibold">Revenue recovered</div>
          <div className="font-display text-3xl font-extrabold mt-1">{jmd(d.revenue_recovered || 0)}</div>
        </div>
        <div className="text-xs text-white/60 text-right max-w-[45%]">Value of carts that converted after a win-back nudge</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Carts nudged" value={d.nudged} testid="cr-nudged" />
        <Stat label={`Recovered (${d.recovery_rate}%)`} value={d.recovered} testid="cr-recovered" cls="text-emerald" />
        <Stat label="Still open" value={d.open} testid="cr-open" cls="text-coral" />
        <Stat label="Perks granted" value={d.perks_granted} testid="cr-perks" />
      </div>
      <p className="text-xs text-muted-foreground" data-testid="cr-points-line">
        Perk points outstanding: <span className="font-semibold text-ink">{d.points_outstanding.toLocaleString()}</span> ·
        reclaimed on expiry: <span className="font-semibold text-ink">{d.points_reclaimed.toLocaleString()}</span> ·
        {d.perks_expired} expired · {d.stage2} follow-ups sent
      </p>
      <div className="rounded-xl border border-border bg-card p-4" data-testid="cr-trend-chart">
        <div className="text-xs font-semibold text-muted-foreground mb-3">Last 30 days · nudged vs recovered</div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Bar dataKey="nudged" fill="#0B131F" radius={[3, 3, 0, 0]} />            <Bar dataKey="recovered" fill="#10B981" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function WeeklyEmailPanel() {
  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/weekly-email").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/weekly-email", { enabled: cfg.enabled, weekday: cfg.weekday, hour: cfg.hour }); toast.success("Schedule saved"); }
    catch { toast.error("Could not save schedule"); }
    setBusy(false);
  };
  const sendTest = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/weekly-email/send-now?test=true"); toast.success(`Test email sent (${data.sent})`); }
    catch { toast.error("Could not send test"); }
    setBusy(false);
  };
  const sendDealTest = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/deal-reminder/send-now?test=true"); toast.success(`Deal reminder test sent (${data.sent})`); }
    catch { toast.error("Could not send deal reminder"); }
    setBusy(false);
  };
  const sendCountdownTest = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/deal-countdown/send-now?test=true"); toast.success(`Countdown test sent (${data.sent}, ${data.hours_left}h left)`); }
    catch { toast.error("Could not send countdown"); }
    setBusy(false);
  };
  const sendAll = async () => {
    if (!window.confirm("Send the New This Week email to ALL eligible members now?")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/weekly-email/send-now?test=false"); toast.success(`Sent to ${data.sent} member(s)`); }
    catch { toast.error("Could not send"); }
    setBusy(false);
  };
  if (!cfg) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald" size={28} /></div>;
  return (
    <div className="bg-card rounded-2xl border border-border p-6 max-w-lg space-y-4" data-testid="weekly-email-panel">
      <div>
        <h3 className="font-display font-bold text-lg">New This Week — Weekly Email</h3>
        <p className="text-sm text-muted-foreground mt-1">Automatically emails SHOP+ members and opted-in shoppers the freshest arrivals. <span className="font-semibold text-ink">{cfg.recipients}</span> member(s) will receive it.</p>
      </div>
      <label className="flex items-center gap-2 text-sm font-semibold" data-testid="weekly-enabled">
        <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} /> Send automatically each week
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs font-semibold text-muted-foreground">Day (Jamaica time)
          <select value={cfg.weekday} onChange={(e) => setCfg({ ...cfg, weekday: Number(e.target.value) })} className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-ink bg-white" data-testid="weekly-day">
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-muted-foreground">Hour (0–23)
          <input type="number" min="0" max="23" value={cfg.hour} onChange={(e) => setCfg({ ...cfg, hour: Math.max(0, Math.min(23, Number(e.target.value))) })} className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-ink" data-testid="weekly-hour" />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={busy} className="bg-ink text-white rounded-xl px-5 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="weekly-save-btn"><Save size={15} /> Save schedule</button>
        <button onClick={sendTest} disabled={busy} className="bg-card border border-border rounded-xl px-5 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="weekly-test-btn"><Send size={15} /> Send test to founder</button>
        <button onClick={sendAll} disabled={busy} className="bg-emerald text-white rounded-xl px-5 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="weekly-sendall-btn"><Mail size={15} /> Send to all now</button>
        <button onClick={sendDealTest} disabled={busy} className="bg-card border border-border rounded-xl px-5 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="deal-reminder-test-btn"><Send size={15} /> Send Deal reminder (test)</button>
        <button onClick={sendCountdownTest} disabled={busy} className="bg-card border border-border rounded-xl px-5 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="deal-countdown-test-btn"><Send size={15} /> Send Deal countdown (test)</button>
      </div>
    </div>
  );
}

function DocumentsPanel() {
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [slug, setSlug] = useState("catalogue");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/documents").then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async () => {
    if (!file) return toast.error("Choose a file first");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("slug", slug || "catalogue"); fd.append("title", title);
    try {
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — it's now live on the site");
      setTitle(""); setFile(null); document.getElementById("doc-file-input").value = ""; load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  const del = async (s) => { try { await api.delete(`/admin/documents/${s}`); load(); toast.success("Removed"); } catch { toast.error("Could not remove"); } };
  return (
    <div className="bg-card rounded-2xl border border-border p-6" data-testid="documents-panel">
      <div className="flex items-center gap-2 mb-1"><FileText size={18} className="text-emerald" /><h3 className="font-display font-bold">Site Documents</h3></div>
      <p className="text-xs text-muted-foreground mb-4">Upload the <b>catalogue</b> (slug <code>catalogue</code>) to power the footer "Download Digital Catalogue" button. PDF, DOC(X), XLSX, CSV, PNG, JPG up to 25MB.</p>
      <div className="grid sm:grid-cols-2 gap-3 items-end mb-5">
        <div>
          <label className="text-xs font-bold uppercase text-muted-foreground">Document slug</label>
          <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="catalogue" className="w-full mt-1 rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="doc-slug" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-muted-foreground">Title (optional)</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 2026 Digital Catalogue" className="w-full mt-1 rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="doc-title" />
        </div>
        <input id="doc-file-input" type="file" accept=".pdf,.doc,.docx,.xlsx,.csv,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files[0])} className="text-sm" data-testid="doc-file" />
        <button onClick={upload} disabled={busy} className="flex items-center justify-center gap-2 bg-ink text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="doc-upload-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload document
        </button>
      </div>
      <div className="space-y-2" data-testid="documents-list">
        {docs.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No documents uploaded yet.</p> : docs.map((d) => (
          <div key={d.slug} className="flex items-center gap-3 border border-border rounded-xl p-3" data-testid={`doc-${d.slug}`}>
            <FileText size={18} className="text-emerald shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate">{d.title} <span className="text-[11px] text-muted-foreground">({d.slug})</span></div>
              <div className="text-[11px] text-muted-foreground truncate">{d.filename} · {new Date(d.updated_at).toLocaleString()}</div>
            </div>
            <a href={d.url.startsWith("http") ? d.url : `${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-emerald" data-testid={`doc-open-${d.slug}`}>Open</a>
            <button onClick={() => del(d.slug)} className="text-muted-foreground hover:text-coral" data-testid={`doc-del-${d.slug}`}><X size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuotesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [billed, setBilled] = useState({});
  const [billing, setBilling] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const load = () => api.get("/admin/quotes").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/admin/logistics").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const products = {};
      cfg.products.forEach((p) => { products[p.id] = { case_weight: p.case_weight, cases_per_pallet: p.cases_per_pallet, price_per_case: p.price_per_case ?? null }; });
      await api.put("/admin/logistics", { products, containers: cfg.containers, pallet_consolidation: cfg.pallet_consolidation, optimization_mode: cfg.optimization_mode, currency: cfg.currency });
      toast.success("Logistics settings saved");
    } catch { toast.error("Could not save settings"); }
    setSavingCfg(false);
  };
  const setP = (i, f, v) => setCfg((c) => ({ ...c, products: c.products.map((p, j) => j === i ? { ...p, [f]: v } : p) }));
  const setC = (i, f, v) => setCfg((c) => ({ ...c, containers: c.containers.map((x, j) => j === i ? { ...x, [f]: v } : x) }));
  const setCI = (i, f, v) => setCfg((c) => ({ ...c, containers: c.containers.map((x, j) => j === i ? { ...x, internal: { ...(x.internal || {}), [f]: v } } : x) }));

  const bill = async (r) => {
    const amt = window.prompt(`Amount in JMD to bill for quote ${r.ref} (charged in USD at 165):`);
    if (amt === null || !Number(amt)) return;
    setBilling(r.ref);
    try {
      const { data } = await api.post(`/admin/quotes/${r.ref}/pay-link`, { amount: Number(amt) });
      setBilled((b) => ({ ...b, [r.ref]: data.checkout_url }));
      toast.success(data.emailed ? "Pay link created & emailed to customer" : "Pay link created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create pay link"); }
    setBilling("");
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={26} /></div>;
  return (
    <div className="space-y-6" data-testid="quotes-panel">
      {cfg && (
        <div className="bg-card rounded-2xl border border-border p-6" data-testid="logistics-settings">
          <div className="flex items-center gap-2 mb-4"><Container size={18} className="text-emerald" /><h3 className="font-display font-bold">Logistics settings</h3>
            <button onClick={saveCfg} disabled={savingCfg} className="ml-auto flex items-center gap-1.5 bg-emerald text-white rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="logistics-save"><Save size={14} /> Save</button>
          </div>
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={cfg.pallet_consolidation === "allowed"} onChange={(e) => setCfg({ ...cfg, pallet_consolidation: e.target.checked ? "allowed" : "not_allowed" })} data-testid="logistics-consolidation" />
            Allow mixed-product pallet consolidation
          </label>
          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
            <label className="flex items-center gap-2">Optimisation
              <select value={cfg.optimization_mode || "min_containers"} onChange={(e) => setCfg({ ...cfg, optimization_mode: e.target.value })} className="rounded-lg border border-border px-2 py-1.5" data-testid="logistics-mode">
                <option value="min_containers">Minimum containers</option>
                <option value="min_cost">Minimum cost</option>
                <option value="max_utilization">Maximum utilisation</option>
                <option value="balanced">Balanced</option>
              </select>
            </label>
            <label className="flex items-center gap-2">Currency
              <input value={cfg.currency || "USD"} onChange={(e) => setCfg({ ...cfg, currency: e.target.value })} className="w-20 rounded-lg border border-border px-2 py-1.5" data-testid="logistics-currency" />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-bold uppercase text-muted-foreground mb-2">Products — weight lb · cases/pallet · J$/case</div>
              <div className="space-y-2">
                {cfg.products.map((p, i) => (
                  <div key={p.id} className="flex items-center gap-2 text-sm">
                    <span className="w-20 font-semibold">{p.label}</span>
                    <input type="number" title="Case weight (lb)" value={p.case_weight ?? ""} onChange={(e) => setP(i, "case_weight", e.target.value === "" ? null : Number(e.target.value))} className="w-16 rounded-lg border border-border px-2 py-1.5" data-testid={`lg-weight-${p.id}`} />
                    <input type="number" title="Cases per pallet" placeholder="cpp" value={p.cases_per_pallet ?? ""} onChange={(e) => setP(i, "cases_per_pallet", e.target.value === "" ? null : Number(e.target.value))} className="w-16 rounded-lg border border-border px-2 py-1.5" data-testid={`lg-cpp-${p.id}`} />
                    <input type="number" title="Price per case (J$)" placeholder="J$" value={p.price_per_case ?? ""} onChange={(e) => setP(i, "price_per_case", e.target.value === "" ? null : Number(e.target.value))} className="w-20 rounded-lg border border-border px-2 py-1.5" data-testid={`lg-price-${p.id}`} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase text-muted-foreground mb-2">Containers — cap · payload lb · interior L/W/H in · vol%</div>
              <div className="space-y-2">
                {cfg.containers.map((c, i) => (
                  <div key={c.id} className="border border-border rounded-lg p-2 space-y-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-24 font-semibold truncate">{c.name}</span>
                      <input type="number" title="Pallet capacity" value={c.pallet_capacity} onChange={(e) => setC(i, "pallet_capacity", Number(e.target.value))} className="w-14 rounded-lg border border-border px-2 py-1.5" data-testid={`lg-cap-${c.id}`} />
                      <input type="number" title="Payload (lb)" value={c.payload_lb} onChange={(e) => setC(i, "payload_lb", Number(e.target.value))} className="w-24 rounded-lg border border-border px-2 py-1.5" data-testid={`lg-pay-${c.id}`} />
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] pl-1">
                      <input type="number" title="Interior length (in)" placeholder="L" value={c.internal?.length_in ?? ""} onChange={(e) => setCI(i, "length_in", e.target.value === "" ? null : Number(e.target.value))} className="w-14 rounded border border-border px-1.5 py-1" data-testid={`lg-len-${c.id}`} />
                      <input type="number" title="Interior width (in)" placeholder="W" value={c.internal?.width_in ?? ""} onChange={(e) => setCI(i, "width_in", e.target.value === "" ? null : Number(e.target.value))} className="w-14 rounded border border-border px-1.5 py-1" data-testid={`lg-wid-${c.id}`} />
                      <input type="number" title="Interior height (in)" placeholder="H" value={c.internal?.height_in ?? ""} onChange={(e) => setCI(i, "height_in", e.target.value === "" ? null : Number(e.target.value))} className="w-14 rounded border border-border px-1.5 py-1" data-testid={`lg-hgt-${c.id}`} />
                      <input type="number" title="Usable volume %" placeholder="vol%" value={c.usable_volume_pct ?? ""} onChange={(e) => setC(i, "usable_volume_pct", e.target.value === "" ? null : Number(e.target.value))} className="w-16 rounded border border-border px-1.5 py-1" data-testid={`lg-vol-${c.id}`} />
                      <input type="number" title={`Cost per container (${cfg.currency || "USD"})`} placeholder="cost" value={c.cost ?? ""} onChange={(e) => setC(i, "cost", e.target.value === "" ? null : Number(e.target.value))} className="w-20 rounded border border-border px-1.5 py-1" data-testid={`lg-cost-${c.id}`} />
                      <span className="text-muted-foreground ml-1">{c.usable_volume_cuft ? `${c.usable_volume_cuft} ft³` : ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center gap-2 mb-4"><ClipboardList size={18} className="text-emerald" /><h3 className="font-display font-bold">Export Quotes</h3><span className="ml-auto text-sm text-muted-foreground">{rows.length} total</span></div>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No quote requests yet.</p> : (
          <div className="space-y-3" data-testid="quotes-list">
            {rows.map((r) => {
              const s = r.result.summary || {}; const rc = r.result.recommendation || {};
              return (
                <div key={r.id} className="border border-border rounded-xl p-4" data-testid={`quote-${r.ref}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-sn font-bold text-ink">{r.ref}</span>
                    <span className="font-semibold">{r.name}</span>
                    {r.company && <span className="text-xs text-muted-foreground">· {r.company}</span>}
                    <span className="ml-auto text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{r.email || "—"} · {r.phone || "—"} · {r.country || "—"}</div>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs">
                    {(r.result.lines || []).map((l) => <span key={l.size} className="bg-secondary rounded-full px-2.5 py-1 font-semibold">{l.label}: {l.cases} · {l.total_weight?.toLocaleString()} lb{l.pallets != null ? ` · ${l.pallets} plt` : ""}</span>)}
                  </div>
                  <div className="mt-2 text-sm font-semibold flex flex-wrap gap-4">
                    <span>{s.total_cases} cases</span>
                    <span>{s.total_weight_lb?.toLocaleString()} lb</span>
                    <span>{s.rounded_pallets} pallets</span>
                    {rc.name && <span className="text-emerald">{rc.qty} × {rc.name}</span>}
                    {rc.status === "incomplete" && <span className="text-gold">config needed</span>}
                  </div>
                  {r.note && <div className="mt-2 text-xs text-muted-foreground italic">"{r.note}"</div>}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button onClick={() => bill(r)} disabled={billing === r.ref} data-testid={`quote-bill-${r.ref}`} className="flex items-center gap-1.5 bg-ink text-white rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60">
                      {billing === r.ref ? <Loader2 size={13} className="animate-spin" /> : <DollarSign size={13} />} Bill via Fygaro
                    </button>
                    {r.email && <span className="text-[11px] text-muted-foreground">emails {r.email}</span>}
                  </div>
                  {billed[r.ref] && (
                    <div className="mt-2 flex items-center gap-3 border border-border rounded-lg p-2" data-testid={`quote-paylink-${r.ref}`}>
                      <QRCodeCanvas value={billed[r.ref]} size={72} />
                      <div className="min-w-0">
                        <a href={billed[r.ref]} target="_blank" rel="noreferrer" className="text-emerald text-xs font-semibold break-all">{billed[r.ref]}</a>
                        <button onClick={() => { navigator.clipboard?.writeText(billed[r.ref]); toast.success("Copied"); }} className="ml-2 text-[11px] underline">Copy</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AIAnalyst() {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [snap, setSnap] = useState(null);
  const [q, setQ] = useState("");
  const suggestions = [
    "What products are trending this week?",
    "Which categories are underperforming?",
    "What are customers searching for that we don't stock?",
    "Forecast sales for next month",
    "Which items are low on stock and need restocking?",
  ];
  const run = async (question) => {
    const userMsg = (question || "").trim();
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    if (userMsg) setMessages((m) => [...m, newMsg("user", userMsg)]);
    setLoading(true); setQ("");
    try {
      const { data } = await api.post("/admin/ai-analyst", { question: question || null, history });
      setMessages((m) => [...m, newMsg("ai", data.answer)]);
      setSnap(data.snapshot);
    } catch { toast.error("AI Analyst is unavailable right now"); }
    setLoading(false);
  };
  const brief = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/morning-brief");
      setMessages((m) => [...m, newMsg("user", "Today's Founder Morning Brief"), newMsg("ai", data.brief)]);
      setSnap(data.snapshot);
    } catch { toast.error("Morning Brief is unavailable right now"); }
    setLoading(false);
  };
  return (
    <div className="space-y-6" data-testid="ai-analyst-panel">
      <div className="bg-gradient-to-br from-ink to-ink/90 text-white rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1"><Sparkles size={20} className="text-gold" /><h3 className="font-display font-bold text-lg">SHOP NOW AI Business Analyst</h3></div>
        <p className="text-sm text-white/70 mb-4">Ask about performance, trends, demand and forecasts — grounded in your live SHOP NOW data.</p>
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !loading && run(q)}
            placeholder="Ask anything, e.g. Which categories are underperforming?"
            className="flex-1 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 px-4 py-3 text-sm" data-testid="ai-analyst-input" />
          <button onClick={() => run(q)} disabled={loading} className="flex items-center gap-2 bg-gold text-ink font-semibold rounded-xl px-5 py-3 text-sm disabled:opacity-60" data-testid="ai-analyst-ask-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Ask
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => run("")} disabled={loading} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 text-xs font-semibold" data-testid="ai-analyst-analyze-now">
            <Sparkles size={13} /> Analyze now
          </button>
          <button onClick={brief} disabled={loading} className="flex items-center gap-1.5 bg-gold text-ink hover:opacity-90 rounded-full px-3 py-1.5 text-xs font-semibold" data-testid="ai-morning-brief-btn">
            <Sparkles size={13} /> Morning Brief
          </button>
          {suggestions.map((s) => (
            <button key={s} onClick={() => { setQ(s); run(s); }} disabled={loading} className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 text-xs" data-testid={`ai-suggestion-${s.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}>
              <Lightbulb size={13} className="text-gold" /> {s}
            </button>
          ))}
        </div>
      </div>

      {snap && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ai-analyst-stats">
          <AIStat label="Revenue (J$)" value={jmd(snap.revenue_jmd)} testid="stat-revenue" />
          <AIStat label="GMV (J$)" value={jmd(snap.gmv_jmd)} testid="stat-gmv" />
          <AIStat label="Orders" value={snap.orders_total} testid="stat-orders" />
          <AIStat label="Avg Order (J$)" value={jmd(snap.aov_jmd)} testid="stat-aov" />
          <AIStat label="Low Stock" value={snap.low_stock_count} testid="stat-lowstock" />
          <AIStat label="Customers" value={snap.customers} testid="stat-customers" />
        </div>
      )}

      {(messages.length > 0 || loading) && (
        <div className="space-y-3" data-testid="ai-analyst-thread">
          {messages.map((m) => (
            m.role === "user" ? (
              <div key={m.id} className="flex justify-end"><div className="bg-ink text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[80%]" data-testid="ai-analyst-user-msg">{m.text}</div></div>
            ) : (
              <div key={m.id} className="bg-card rounded-2xl border border-border p-5">
                <div className="flex items-center gap-2 mb-2 text-emerald"><Sparkles size={15} /><span className="text-xs font-semibold">SHOP NOW AI</span></div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-analyst-answer">{m.text}</div>
              </div>
            )
          ))}
          {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card rounded-2xl border border-border p-4"><Loader2 className="animate-spin text-emerald" size={18} /> Analyzing your SHOP NOW data…</div>}
        </div>
      )}
    </div>
  );
}

function TrustCenter() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/trust").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const resolve = async (id) => {
    try { await api.patch(`/admin/trust/${id}`, { status: "resolved" }); toast.success("Report resolved"); load(); }
    catch { toast.error("Could not resolve"); }
  };
  if (!data) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={26} /></div>;
  return (
    <div className="space-y-6" data-testid="trust-panel">
      <div className="grid grid-cols-3 gap-3">
        <AIStat label="Open Reports" value={data.open_reports} testid="trust-open" />
        <AIStat label="Velocity Flags (24h)" value={data.velocity_flags.length} testid="trust-velocity" />
        <AIStat label="High-Value Orders (24h)" value={data.high_value_orders_24h} testid="trust-hv" />
      </div>
      {data.velocity_flags.length > 0 && (
        <div className="bg-card rounded-2xl border border-border p-5">
          <div className="flex items-center gap-2 mb-3 text-gold"><AlertTriangle size={16} /><h3 className="font-display font-bold text-sm">Velocity Flags — accounts with 5+ orders in 24h</h3></div>
          {data.velocity_flags.map((v) => (
            <div key={v.user_id} className="flex items-center justify-between py-2 text-sm border-b border-border/60 last:border-0">
              <span className="font-mono-sn text-xs">{v.user_id}</span>
              <span>{v.orders_24h} orders · {jmd(v.value_jmd)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="bg-card rounded-2xl border border-border p-5">
        <h3 className="font-display font-bold mb-3">Reported Listings</h3>
        {data.reports.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No reports filed.</p> : (
          <div className="divide-y divide-border/60" data-testid="trust-reports">
            {data.reports.map((r) => (
              <div key={r.id} className="py-3 flex items-start justify-between gap-3" data-testid={`report-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{r.product_name || r.product_id || "General report"} <span className="ml-1 text-[10px] bg-coral/15 text-coral px-1.5 py-0.5 rounded capitalize">{r.reason}</span></div>
                  <div className="text-xs text-muted-foreground truncate">{r.details}{r.reporter ? ` · ${r.reporter}` : ""}</div>
                </div>
                {r.status === "open" ? (
                  <button onClick={() => resolve(r.id)} className="shrink-0 text-xs font-semibold bg-emerald text-white rounded-lg px-3 py-1.5" data-testid={`resolve-report-${r.id}`}>Resolve</button>
                ) : <span className="shrink-0 text-xs text-emerald font-semibold">Resolved</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EnquiriesPanel() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/enquiries").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const handle = async (id) => {
    try { await api.patch(`/admin/enquiries/${id}`); toast.success("Marked handled"); load(); }
    catch { toast.error("Could not update"); }
  };
  if (!rows) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={26} /></div>;
  return (
    <div className="space-y-4" data-testid="enquiries-panel">
      <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-6 text-white">
        <div className="flex items-center gap-3 relative">
          <span className="h-11 w-11 rounded-xl bg-gold grid place-items-center text-ink"><Sailboat size={22} /></span>
          <div><h3 className="font-display text-xl font-bold">Product Enquiries</h3><p className="text-white/60 text-sm">Yacht &amp; price-on-request leads from customers.</p></div>
          <span className="ml-auto font-display font-bold text-2xl">{rows.filter((r) => r.status === "new").length} <span className="text-sm font-normal text-white/60">new</span></span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No enquiries yet.</div>
      ) : rows.map((e) => (
        <div key={e.id} className="bg-card rounded-2xl border border-border p-5" data-testid="enquiry-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{e.product_name || "Product"}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${e.status === "new" ? "bg-coral/15 text-coral" : "bg-emerald/15 text-emerald"}`}>{e.status}</span>
              </div>
              <div className="text-sm mt-1.5">{e.name} · {e.email} · {e.phone}</div>
              <div className="text-xs text-muted-foreground mt-1">{e.viewing_date ? `Preferred viewing: ${e.viewing_date} · ` : ""}{e.message || "No message"}</div>
            </div>
            {e.status === "new" && (
              <button onClick={() => handle(e.id)} className="shrink-0 text-sm font-semibold bg-emerald text-white rounded-lg px-3 py-2" data-testid={`enquiry-handle-${e.id}`}>Mark Handled</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


function CatalogueManager() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [mains, setMains] = useState([]);
  const [q, setQ] = useState("");
  const [mc, setMc] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [csv, setCsv] = useState("");
  const [importing, setImporting] = useState(false);
  const [rs, setRs] = useState("");
  const [sel, setSel] = useState(new Set());
  const [bulkMc, setBulkMc] = useState("");
  const [bulking, setBulking] = useState(false);
  const [aiCat, setAiCat] = useState("");
  const [aiSug, setAiSug] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [applyingAi, setApplyingAi] = useState(false);
  const [catalogueDoc, setCatalogueDoc] = useState(null);
  const [descBusy, setDescBusy] = useState(false);

  const suggestPrices = async () => {
    if (!aiCat) { toast.error("Pick a category first"); return; }
    setAiBusy(true); setAiSug(null);
    try {
      const { data } = await api.post("/admin/catalogue/ai-price-suggest", { main_category: aiCat, limit: 30 });
      if (!data.suggestions.length) { toast.info("No 'price on request' items left in this category"); }
      setAiSug(data.suggestions);
    } catch { toast.error("Pricing AI is unavailable right now"); }
    setAiBusy(false);
  };
  const applyAiPrices = async () => {
    const items = (aiSug || []).filter((s) => Number(s.suggested_price_jmd) > 0).map((s) => ({ id: s.id, price: Number(s.suggested_price_jmd) }));
    if (!items.length) { toast.error("No valid prices to apply"); return; }
    setApplyingAi(true);
    try {
      const { data } = await api.post("/admin/catalogue/apply-prices", { items });
      toast.success(`Published prices for ${data.updated} item(s)`);
      setAiSug(null); load();
    } catch { toast.error("Could not apply prices"); }
    setApplyingAi(false);
  };

  const load = async () => {
    setLoading(true);
    const params = { skip: page * PER_PAGE, limit: PER_PAGE };
    if (q) params.q = q;
    if (mc) params.main_category = mc;
    if (rs) params.review_status = rs;
    const { data } = await api.get("/admin/catalogue", { params });
    setItems(data.items); setTotal(data.total); setMains(data.main_categories);
    setSel(new Set());
    setLoading(false);
  };
  useEffect(() => { load(); }, [page, mc, rs]);
  useEffect(() => { api.get("/documents").then(({ data }) => setCatalogueDoc(data.find((d) => d.slug === "catalogue") || null)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setPage(0); load(); }, 400); return () => clearTimeout(t); }, [q]);

  const openEdit = (p) => { setEdit(p.id); setForm({ name: p.name, brand: p.brand || "", sku: p.sku || "", main_category: p.main_category || "", price: p.price || 0, description: p.description || "" }); };
  const save = async () => {
    try {
      await api.patch(`/admin/catalogue/${edit}`, { ...form, price: Number(form.price) });
      toast.success("Product updated");
      setEdit(null); load();
    } catch { toast.error("Could not save changes"); }
  };
  const writeDesc = async () => {
    if (!form.name) { toast.error("Enter a product name first"); return; }
    setDescBusy(true);
    try {
      const { data } = await api.post("/admin/catalogue/ai-describe", { name: form.name, brand: form.brand, main_category: form.main_category });
      setForm((f) => ({ ...f, description: data.description }));
      toast.success("Claude wrote a description — review and save");
    } catch { toast.error("Description AI is unavailable right now"); }
    setDescBusy(false);
  };
  const toggleHide = async (p) => {
    try { await api.patch(`/admin/catalogue/${p.id}`, { hidden: !p.hidden }); toast.success(p.hidden ? "Product shown" : "Product hidden"); load(); }
    catch { toast.error("Could not update visibility"); }
  };
  const toggleFlag = async (p) => {
    const next = p.review_status === "needs_review" ? "" : "needs_review";
    try { await api.patch(`/admin/catalogue/${p.id}`, { review_status: next || "cleared" }); toast.success(next ? "Flagged for review" : "Marked reviewed"); load(); }
    catch { toast.error("Could not update flag"); }
  };
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelAll = () => setSel((s) => s.size === items.length ? new Set() : new Set(items.map((p) => p.id)));
  const applyBulk = async (payload, label) => {
    if (sel.size === 0) { toast.error("Select items first"); return; }
    setBulking(true);
    try {
      const { data } = await api.post("/admin/catalogue/bulk", { ids: [...sel], ...payload });
      toast.success(`${label}: ${data.updated} item(s)`);
      load();
    } catch { toast.error("Bulk action failed"); }
    setBulking(false);
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/catalogue/price-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "shopnow_price_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setCsv(String(r.result || "")); r.readAsText(f);
  };
  const runImport = async () => {
    if (!csv.trim()) { toast.error("Paste or upload a CSV first"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/catalogue/prices", { csv });
      toast.success(`Prices updated: ${data.updated} · skipped ${data.skipped}`);
      setCsv(""); load();
    } catch { toast.error("Price import failed"); }
    setImporting(false);
  };

  const pages = Math.ceil(total / PER_PAGE) || 1;

  return (
    <div className="space-y-6" data-testid="catalogue-panel">
      <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-5 text-white" data-testid="founder-catalogue-panel">
        <div className="flex items-center gap-3 relative flex-wrap">
          <span className="h-11 w-11 rounded-xl bg-emerald grid place-items-center"><Download size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-display font-bold">Digital Catalogue (Founder Only)</h3>
            <p className="text-white/60 text-xs">{catalogueDoc ? "Serving the catalogue you uploaded in Site Documents." : "No catalogue uploaded yet — upload one under Site Documents (slug \"catalogue\")."}</p>
          </div>
          {catalogueDoc ? (
            <a href={catalogueDoc.url.startsWith("http") ? catalogueDoc.url : `${process.env.REACT_APP_BACKEND_URL}${catalogueDoc.url}`}
              target="_blank" rel="noreferrer" data-testid="founder-catalogue-download"
              className="ml-auto inline-flex items-center gap-2 text-sm font-semibold bg-emerald text-white rounded-full px-4 py-2 hover:bg-white hover:text-ink transition-colors">
              <Download size={16} /> Download Catalogue PDF
            </a>
          ) : (
            <span className="ml-auto text-xs text-white/50 italic">No file yet</span>
          )}
        </div>
      </div>
      <div className="bg-card rounded-2xl border border-border p-6" data-testid="price-import-panel">
        <div className="flex items-center gap-2 mb-1"><DollarSign size={18} className="text-emerald" /><h3 className="font-display font-bold">Real Price Import</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Download the template with every item code, fill the <b>price_jmd</b> column, then upload or paste it back to publish real J$ prices.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={downloadTemplate} className="flex items-center gap-2 bg-ink text-white rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="download-template-btn"><Download size={16} /> Download Price Template</button>
          <label className="flex items-center gap-2 border border-border rounded-xl px-4 py-2.5 text-sm font-semibold cursor-pointer" data-testid="upload-csv-label"><Upload size={16} /> Upload Filled CSV
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="upload-csv-input" />
          </label>
        </div>
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="item_code,product_name,main_category,price_jmd&#10;B1340MB,...,,4500" rows={3} className="w-full rounded-xl border border-border px-3 py-2 text-sm font-mono-sn" data-testid="price-csv-textarea" />
        <button onClick={runImport} disabled={importing} className="mt-3 flex items-center gap-2 bg-emerald text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="import-prices-btn">
          {importing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Import Prices
        </button>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6" data-testid="ai-price-panel">
        <div className="flex items-center gap-2 mb-1"><Sparkles size={18} className="text-gold" /><h3 className="font-display font-bold">AI Fair-Price Suggestions</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Pick a category and let the Pricing AI propose fair J$ retail prices for its "price on request" items. Review, tweak, then publish to make them instantly buyable.</p>
        <div className="flex flex-wrap gap-2">
          <select value={aiCat} onChange={(e) => setAiCat(e.target.value)} className="rounded-xl border border-border px-3 py-2.5 text-sm min-w-[220px]" data-testid="ai-price-category">
            <option value="">Choose a category…</option>
            {mains.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button onClick={suggestPrices} disabled={aiBusy || !aiCat} className="flex items-center gap-2 bg-ink text-white rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="ai-price-suggest-btn">
            {aiBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Suggest prices with AI
          </button>
        </div>
        {aiSug && aiSug.length > 0 && (
          <div className="mt-4" data-testid="ai-price-suggestions">
            <div className="max-h-72 overflow-y-auto divide-y divide-border/60 border border-border rounded-xl">
              {aiSug.map((s, i) => (
                <div key={s.id} className="flex items-center gap-3 px-3 py-2" data-testid={`ai-price-row-${s.id}`}>
                  <span className="text-sm flex-1 truncate">{s.name}</span>
                  <span className="text-emerald text-sm font-semibold">J$</span>
                  <input type="number" value={s.suggested_price_jmd}
                    onChange={(e) => setAiSug((arr) => arr.map((x, j) => j === i ? { ...x, suggested_price_jmd: e.target.value } : x))}
                    className="w-28 rounded-lg border border-border px-2 py-1.5 text-sm text-right" data-testid={`ai-price-input-${s.id}`} />
                </div>
              ))}
            </div>
            <button onClick={applyAiPrices} disabled={applyingAi} className="mt-3 flex items-center gap-2 bg-emerald text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="ai-price-apply-btn">
              {applyingAi ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Publish {aiSug.filter((s) => Number(s.suggested_price_jmd) > 0).length} prices
            </button>
          </div>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center gap-2 mb-4"><ClipboardList size={18} className="text-emerald" /><h3 className="font-display font-bold">Catalogue Review Queue</h3><span className="text-xs text-muted-foreground">({total} items)</span></div>
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU or brand" className="w-full rounded-xl border border-border pl-9 pr-3 py-2.5 text-sm" data-testid="catalogue-search" />
          </div>
          <select value={mc} onChange={(e) => { setPage(0); setMc(e.target.value); }} className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="catalogue-mc-filter">
            <option value="">All Categories</option>
            {mains.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={rs} onChange={(e) => { setPage(0); setRs(e.target.value); }} className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="catalogue-rs-filter">
            <option value="">All Statuses</option>
            <option value="needs_review">Needs Review</option>
          </select>
        </div>

        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4 bg-secondary/40 rounded-xl px-3 py-2.5" data-testid="bulk-toolbar">
            <button onClick={toggleSelAll} className="flex items-center gap-1.5 text-sm font-semibold" data-testid="bulk-select-all">
              {sel.size === items.length && items.length > 0 ? <CheckSquare size={16} className="text-emerald" /> : <Square size={16} />}
              {sel.size > 0 ? `${sel.size} selected` : "Select all"}
            </button>
            <div className="h-4 w-px bg-border mx-1" />
            <select value={bulkMc} onChange={(e) => setBulkMc(e.target.value)} disabled={sel.size === 0} className="rounded-lg border border-border px-2.5 py-1.5 text-sm disabled:opacity-50" data-testid="bulk-mc-select">
              <option value="">Move to category…</option>
              {mains.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <button onClick={() => bulkMc && applyBulk({ main_category: bulkMc }, "Recategorised")} disabled={!bulkMc || sel.size === 0 || bulking} className="flex items-center gap-1 bg-ink text-white rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50" data-testid="bulk-apply-mc"><Tag size={14} /> Apply</button>
            <button onClick={() => applyBulk({ review_status: "needs_review" }, "Flagged")} disabled={sel.size === 0 || bulking} className="flex items-center gap-1 border border-border rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50" data-testid="bulk-flag"><Flag size={14} /> Flag</button>
            <button onClick={() => applyBulk({ review_status: "cleared" }, "Cleared")} disabled={sel.size === 0 || bulking} className="flex items-center gap-1 border border-border rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50" data-testid="bulk-clear-flag"><Check size={14} /> Mark reviewed</button>
          </div>
        )}

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={28} /></div>
        ) : (
          <div className="divide-y divide-border/60" data-testid="catalogue-list">
            {items.map((p) => (
              <div key={p.id} className="py-3" data-testid={`catalogue-row-${p.id}`}>
                {edit === p.id ? (
                  <div className="space-y-2 bg-secondary/40 rounded-xl p-4">
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="edit-name" />
                      <input value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} placeholder="Item code / SKU" className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="edit-sku" />
                      <input value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} placeholder="Brand" className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="edit-brand" />
                      <select value={form.main_category} onChange={(e) => setForm((f) => ({ ...f, main_category: e.target.value }))} className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="edit-mc">
                        <option value="">— Category —</option>
                        {mains.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <input type="number" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="Price J$ (0 = on request)" className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="edit-price" />
                    </div>
                    <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" rows={2} className="w-full rounded-lg border border-border px-3 py-2 text-sm" data-testid="edit-desc" />
                    <button onClick={writeDesc} disabled={descBusy} className="flex items-center gap-1.5 text-xs font-semibold text-emerald hover:text-emerald/80 disabled:opacity-60" data-testid="edit-ai-desc-btn">
                      {descBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {descBusy ? "Claude is writing…" : "Write description with Claude AI"}
                    </button>
                    <div className="flex gap-2">
                      <button onClick={save} className="flex items-center gap-1.5 bg-emerald text-white rounded-lg px-4 py-2 text-sm font-semibold" data-testid="edit-save-btn"><Save size={15} /> Save</button>
                      <button onClick={() => setEdit(null)} className="flex items-center gap-1.5 border border-border rounded-lg px-4 py-2 text-sm font-semibold" data-testid="edit-cancel-btn"><X size={15} /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <button onClick={() => toggleSel(p.id)} className="shrink-0 text-muted-foreground hover:text-emerald" data-testid={`select-${p.id}`}>
                      {sel.has(p.id) ? <CheckSquare size={18} className="text-emerald" /> : <Square size={18} />}
                    </button>
                    <img src={assetUrl(p.image)} alt={p.name} className={`h-12 w-12 rounded-lg object-cover bg-secondary shrink-0 ${p.hidden ? "opacity-40" : ""}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{p.name}
                        {p.hidden && <span className="ml-2 text-[10px] bg-coral/15 text-coral px-1.5 py-0.5 rounded">HIDDEN</span>}
                        {p.review_status === "needs_review" && <span className="ml-2 text-[10px] bg-gold/15 text-gold px-1.5 py-0.5 rounded" data-testid={`needs-review-badge-${p.id}`}>NEEDS REVIEW</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{p.sku ? `${p.sku} · ` : ""}{p.brand || "—"} · {p.main_category || "Uncategorised"}</div>
                    </div>
                    <span className="text-sm font-bold shrink-0 mr-1">{p.price_on_request || !p.price ? <span className="text-gold text-xs">On request</span> : jmd(p.price)}</span>
                    <button onClick={() => toggleFlag(p)} className={`flex items-center gap-1 text-sm font-semibold border rounded-lg px-2.5 py-1.5 shrink-0 ${p.review_status === "needs_review" ? "border-gold text-gold bg-gold/10" : "border-border"}`} data-testid={`flag-btn-${p.id}`}><Flag size={14} /></button>
                    <button onClick={() => openEdit(p)} className="flex items-center gap-1 text-sm font-semibold bg-ink text-white rounded-lg px-3 py-1.5 shrink-0" data-testid={`edit-btn-${p.id}`}><Pencil size={14} /> Edit</button>
                    <button onClick={() => toggleHide(p)} className="flex items-center gap-1 text-sm font-semibold border border-border rounded-lg px-3 py-1.5 shrink-0" data-testid={`hide-btn-${p.id}`}>{p.hidden ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                  </div>
                )}
              </div>
            ))}
            {items.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No catalogue items match.</p>}
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
            <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="flex items-center gap-1 text-sm font-semibold disabled:opacity-40" data-testid="catalogue-prev"><ChevronLeft size={16} /> Prev</button>
            <span className="text-sm text-muted-foreground">Page {page + 1} of {pages}</span>
            <button disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 text-sm font-semibold disabled:opacity-40" data-testid="catalogue-next">Next <ChevronRight size={16} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function HealthStat({ label, value }) {
  return (
    <div className="bg-secondary/40 rounded-xl p-3 text-center">
      <div className="font-display font-bold text-xl">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function PlatformOps() {
  const [health, setHealth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sumBusy, setSumBusy] = useState(false);
  const [changes, setChanges] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", category: "feature" });
  const [saving, setSaving] = useState(false);

  const loadAll = () => {
    api.get("/admin/platform/health").then(({ data }) => setHealth(data)).catch(() => {});
    api.get("/admin/platform/changelog").then(({ data }) => setChanges(data)).catch(() => {});
    api.get("/admin/platform/requests").then(({ data }) => setReqs(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const genSummary = async (refresh) => {
    setSumBusy(true);
    try {
      const { data } = await api.post(`/admin/platform/executive-summary${refresh ? "?refresh=true" : ""}`);
      setSummary(data);
    } catch { toast.error("Executive summary is unavailable right now"); }
    setSumBusy(false);
  };
  const submitReq = async () => {
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    setSaving(true);
    try {
      await api.post("/admin/platform/requests", form);
      toast.success("Request submitted");
      setForm({ title: "", description: "", priority: "medium", category: "feature" });
      api.get("/admin/platform/requests").then(({ data }) => setReqs(data)).catch(() => {});
    } catch { toast.error("Could not submit request"); }
    setSaving(false);
  };
  const advance = async (r) => {
    const next = r.status === "open" ? "in_progress" : r.status === "in_progress" ? "done" : "open";
    try {
      await api.patch(`/admin/platform/requests/${r.id}`, { status: next });
      api.get("/admin/platform/requests").then(({ data }) => setReqs(data)).catch(() => {});
    } catch { toast.error("Could not update request"); }
  };

  const statusColor = health?.status === "Operational" ? "bg-emerald" : health?.status === "Degraded" ? "bg-gold" : "bg-coral";
  const catColor = { fix: "bg-coral/15 text-coral", upgrade: "bg-emerald/15 text-emerald", security: "bg-ink text-white", feature: "bg-gold/15 text-gold" };
  const reqColor = { open: "bg-secondary text-ink", in_progress: "bg-gold/15 text-gold", done: "bg-emerald/15 text-emerald" };

  return (
    <div className="space-y-6" data-testid="platform-panel">
      {/* Executive Summary */}
      <div className="bg-card rounded-2xl border border-border p-6" data-testid="exec-summary-card">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <FileText size={18} className="text-emerald" />
          <h3 className="font-display font-bold">Monthly Executive Summary</h3>
          <div className="ml-auto flex gap-2">
            {summary && <button onClick={() => genSummary(true)} disabled={sumBusy} className="flex items-center gap-1.5 border border-border rounded-xl px-3 py-2 text-sm font-semibold disabled:opacity-60" data-testid="exec-summary-refresh"><RefreshCw size={14} className={sumBusy ? "animate-spin" : ""} /> Regenerate</button>}
            {!summary && <button onClick={() => genSummary(false)} disabled={sumBusy} className="flex items-center gap-1.5 bg-emerald text-white rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="exec-summary-generate">{sumBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate summary</button>}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">AI-written summary of completed work, platform health, incidents and next steps — grounded in your live data.</p>
        {summary ? (
          <div className="bg-secondary/40 rounded-xl p-4 text-sm leading-relaxed whitespace-pre-wrap" data-testid="exec-summary-text">{summary.summary}
            <div className="text-[11px] text-muted-foreground mt-3">Month {summary.month} · generated {new Date(summary.generated_at).toLocaleString()}</div>
          </div>
        ) : sumBusy ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/40 rounded-xl p-4"><Loader2 className="animate-spin text-emerald" size={18} /> Compiling your executive summary…</div>
        ) : <p className="text-sm text-muted-foreground py-2">No summary generated yet for this month.</p>}
      </div>

      {/* Platform Health */}
      <div className="bg-card rounded-2xl border border-border p-6" data-testid="platform-health-card">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <HeartPulse size={18} className="text-coral" />
          <h3 className="font-display font-bold">Platform Health & Reliability</h3>
          {health && <span className={`ml-auto text-xs font-bold text-white px-3 py-1 rounded-full ${statusColor}`} data-testid="platform-status">{health.status}</span>}
        </div>
        {!health ? <div className="grid place-items-center py-8"><Loader2 className="animate-spin text-emerald" size={24} /></div> : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
              <HealthStat label="Uptime" value={`${health.uptime_pct}%`} />
              <HealthStat label="Products" value={(health.counts.products || 0).toLocaleString()} />
              <HealthStat label="Orders" value={(health.counts.orders || 0).toLocaleString()} />
              <HealthStat label="Users" value={(health.counts.users || 0).toLocaleString()} />
              <HealthStat label="Merchants" value={health.counts.merchants || 0} />
              <HealthStat label="Open Tickets" value={health.open_tickets || 0} />
              <HealthStat label="Open Incidents" value={health.incidents_open || 0} />
              <HealthStat label="AI Calls (mo)" value={health.ai_calls_month || 0} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 bg-secondary/40 rounded-xl px-4 py-3"><Server size={15} className="text-emerald" /> Database: <b className={health.db_ok ? "text-emerald" : "text-coral"}>{health.db_ok ? "Connected" : "Unreachable"}</b></div>
              <div className="flex items-center gap-2 bg-secondary/40 rounded-xl px-4 py-3"><Download size={15} className="text-emerald" /> Backups: <b>{health.backups}</b></div>
            </div>
          </>
        )}
      </div>

      {/* Request Portal */}
      <div className="bg-card rounded-2xl border border-border p-6" data-testid="request-portal-card">
        <div className="flex items-center gap-2 mb-1"><Inbox size={18} className="text-emerald" /><h3 className="font-display font-bold">Request Portal</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Submit feature requests and support items to your platform team, and track their priority and status.</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-2">
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Request title" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="request-title" />
          <div className="flex gap-2">
            <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="flex-1 rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="request-category">
              <option value="feature">Feature</option><option value="support">Support</option><option value="bug">Bug</option>
            </select>
            <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="flex-1 rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="request-priority">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
          </div>
        </div>
        <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Describe what you need…" rows={2} className="w-full rounded-xl border border-border px-3 py-2.5 text-sm mb-2" data-testid="request-description" />
        <button onClick={submitReq} disabled={saving} className="flex items-center gap-2 bg-ink text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="request-submit"> {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit request</button>
        <div className="mt-4 divide-y divide-border/60" data-testid="request-list">
          {reqs.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No requests yet.</p> : reqs.map((r) => (
            <div key={r.id} className="py-3 flex items-start gap-3" data-testid={`request-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium flex items-center gap-2 flex-wrap">{r.title}
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{r.priority}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{r.category}</span>
                </div>
                {r.description && <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>}
              </div>
              <button onClick={() => advance(r)} className={`shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full capitalize ${reqColor[r.status] || "bg-secondary"}`} data-testid={`request-status-${r.id}`}>{(r.status || "open").replace("_", " ")}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Change Log */}
      <div className="bg-card rounded-2xl border border-border p-6" data-testid="changelog-card">
        <div className="flex items-center gap-2 mb-1"><ScrollText size={18} className="text-emerald" /><h3 className="font-display font-bold">Change Log</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Every fix, upgrade, security update and new feature shipped on your platform.</p>
        <div className="divide-y divide-border/60" data-testid="changelog-list">
          {changes.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No entries yet.</p> : changes.map((c) => (
            <div key={c.id} className="py-3 flex items-start gap-3" data-testid={`changelog-row-${c.id}`}>
              <span className={`shrink-0 mt-0.5 text-[10px] uppercase font-bold px-2 py-0.5 rounded ${catColor[c.category] || "bg-secondary"}`}>{c.category}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{c.title}</div>
                {c.description && <div className="text-xs text-muted-foreground mt-0.5">{c.description}</div>}
                <div className="text-[11px] text-muted-foreground mt-1">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""} · {c.status}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const GMV_DATA = [ { m: "Feb", v: 4.2 }, { m: "Mar", v: 5.1 }, { m: "Apr", v: 4.9 },
  { m: "May", v: 6.8 }, { m: "Jun", v: 8.1 }, { m: "Jul", v: 9.6 },
];

const RISK_META = {
  0: { l: "L0 · Observation", c: "bg-slate-100 text-slate-700" },
  1: { l: "L1 · Low", c: "bg-emerald/10 text-emerald" },
  2: { l: "L2 · Moderate", c: "bg-gold/15 text-yellow-700" },
  3: { l: "L3 · High", c: "bg-coral/15 text-coral" },
  4: { l: "L4 · Critical", c: "bg-red-600/15 text-red-700" },
};
const STATUS_META = {
  proposed: "text-yellow-700 bg-gold/15", executed: "text-emerald bg-emerald/10",
  dismissed: "text-muted-foreground bg-secondary", rolled_back: "text-coral bg-coral/10",
  superseded: "text-muted-foreground bg-secondary",
};

function PrivacyPanel() {
  const [stats, setStats] = useState(null);
  const [reqs, setReqs] = useState([]);
  const load = () => {
    api.get("/admin/consent-stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/data-requests").then(({ data }) => setReqs(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { try { await api.post(`/admin/data-requests/${id}/resolve`, { status: "resolved" }); toast.success("Marked resolved"); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-6" data-testid="privacy-panel">
      <div className="bg-card rounded-2xl border border-border p-6" data-testid="consent-stats">
        <h3 className="font-display font-bold mb-1">Cookie Consent Analytics</h3>
        <p className="text-xs text-muted-foreground mb-4">How visitors respond to the cookie banner.</p>
        {!stats ? <Loader2 className="animate-spin text-emerald" size={20} /> : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["Total responses", stats.total], ["Accepted analytics", stats.accepted_analytics], ["Rejected analytics", stats.rejected_analytics], ["Preferences on", stats.preferences_on]].map(([l, v]) => (
                <div key={l} className="border border-border rounded-xl p-4"><div className="font-display font-bold text-2xl">{v}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>
              ))}
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-1"><span className="font-semibold">Accept rate</span><span className="font-bold text-emerald">{stats.accept_rate}%</span></div>
              <div className="h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-emerald" style={{ width: `${stats.accept_rate}%` }} /></div>
            </div>
          </>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border p-6" data-testid="data-requests-inbox">
        <h3 className="font-display font-bold mb-1">Data Protection Requests</h3>
        <p className="text-xs text-muted-foreground mb-4">Access, erasure and correction requests from guests. Signed-in users self-serve via the portal.</p>
        {reqs.length === 0 ? <p className="text-sm text-muted-foreground py-3 text-center">No requests yet.</p> : (
          <div className="divide-y divide-border/60">
            {reqs.map((r) => (
              <div key={r.id} className="py-3 flex items-start gap-3" data-testid={`data-req-${r.id}`}>
                <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${r.status === "open" ? "bg-gold/15 text-yellow-700" : "bg-emerald/10 text-emerald"}`}>{r.status}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{r.kind} · {r.email}</div>
                  {r.message && <div className="text-[11px] text-muted-foreground">{r.message}</div>}
                  <div className="text-[10px] text-muted-foreground mt-0.5">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
                </div>
                {r.status === "open" && (
                  <button onClick={() => resolve(r.id)} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold shrink-0" data-testid={`data-req-resolve-${r.id}`}><Check size={13} /> Resolve</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SiteContentManager() {
  const [blocks, setBlocks] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ type: "text", title: "", body: "", url: "", section: "home" });
  const [cat, setCat] = useState({ name: "", blurb: "" });
  const load = () => {
    api.get("/admin/site-content").then(({ data }) => setBlocks(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const saveBlock = async () => {
    if (!form.title) { toast.error("Add a title"); return; }
    try { await api.post("/admin/site-content", form); toast.success("Content saved"); setForm({ type: "text", title: "", body: "", url: "", section: "home" }); load(); }
    catch { toast.error("Could not save"); }
  };
  const delBlock = async (id) => { try { await api.delete(`/admin/site-content/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const addCat = async () => {
    if (!cat.name) { toast.error("Category name required"); return; }
    try { await api.post("/admin/categories", cat); toast.success("Category added"); setCat({ name: "", blurb: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delCat = async (slug) => { if (!window.confirm("Remove this category?")) return; try { await api.delete(`/admin/categories/${slug}`); toast.success("Category removed"); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-6" data-testid="site-content-manager">
      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="font-display font-bold mb-1">Site Content</h3>
        <p className="text-xs text-muted-foreground mb-4">Add or remove text, images, videos and sections. Anything you add here is live on the site.</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-3">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="content-type">
            <option value="text">Text</option><option value="image">Image</option><option value="video">Video</option><option value="section">Section</option>
          </select>
          <input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="Section (e.g. home, announcement)" className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="content-section" />
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="rounded-lg border border-border px-3 py-2 text-sm sm:col-span-2" data-testid="content-title" />
          {(form.type === "image" || form.type === "video") && (
            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder={`${form.type} URL`} className="rounded-lg border border-border px-3 py-2 text-sm sm:col-span-2" data-testid="content-url" />
          )}
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Body text" rows={2} className="rounded-lg border border-border px-3 py-2 text-sm sm:col-span-2" data-testid="content-body" />
        </div>
        <button onClick={saveBlock} className="bg-emerald text-white rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5" data-testid="content-save"><Plus size={15} /> Add content</button>
        <div className="mt-4 divide-y divide-border/60">
          {blocks.length === 0 ? <p className="text-sm text-muted-foreground py-3">No custom content yet.</p> : blocks.map((b) => (
            <div key={b.block_id} className="py-2.5 flex items-center gap-3" data-testid={`content-row-${b.block_id}`}>
              <span className="text-[10px] font-bold bg-secondary rounded-full px-2 py-0.5">{b.type}</span>
              <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{b.title || b.block_id}</div><div className="text-[11px] text-muted-foreground truncate">{b.section} · {b.body || b.url}</div></div>
              <button onClick={() => delBlock(b.block_id)} className="text-coral" data-testid={`content-del-${b.block_id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="font-display font-bold mb-1">Categories</h3>
        <p className="text-xs text-muted-foreground mb-4">Create or remove shopping categories. Removed built-in categories are hidden (reversible by re-adding).</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={cat.name} onChange={(e) => setCat({ ...cat, name: e.target.value })} placeholder="New category name" className="rounded-lg border border-border px-3 py-2 text-sm flex-1 min-w-[160px]" data-testid="cat-name" />
          <input value={cat.blurb} onChange={(e) => setCat({ ...cat, blurb: e.target.value })} placeholder="Short blurb (optional)" className="rounded-lg border border-border px-3 py-2 text-sm flex-1 min-w-[160px]" data-testid="cat-blurb" />
          <button onClick={addCat} className="bg-emerald text-white rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5" data-testid="cat-add"><Plus size={15} /> Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => (
            <span key={c.slug} className="inline-flex items-center gap-1.5 bg-secondary rounded-full pl-3 pr-1.5 py-1 text-xs font-medium" data-testid={`cat-chip-${c.slug}`}>
              {c.name}{c.custom && <span className="text-[9px] text-emerald font-bold">NEW</span>}
              <button onClick={() => delCat(c.slug)} className="h-5 w-5 grid place-items-center rounded-full hover:bg-coral hover:text-white" data-testid={`cat-del-${c.slug}`}><X size={12} /></button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function DescriptionJobPanel() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/ai/descriptions/status").then(({ data }) => setS(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);
  const start = async () => { try { const { data } = await api.post("/admin/ai/descriptions/start"); toast.success(data.already_running ? "Already running" : `Started · ${data.total} products queued`); load(); } catch { toast.error("Could not start"); } };
  const stop = async () => { try { await api.post("/admin/ai/descriptions/stop"); toast.success("Stopping after current batch"); } catch { toast.error("Failed"); } };
  if (!s) return null;
  const pct = s.total ? Math.min(100, Math.round((s.processed / s.total) * 100)) : 0;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="ai-descriptions-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="h-10 w-10 rounded-xl bg-emerald/10 text-emerald grid place-items-center"><Sparkles size={18} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-ink">AI Product Descriptions (ChatGPT)</h3>
          <p className="text-xs text-muted-foreground">Writes rich descriptions for imported price-list items in batches of 10. {s.done_total.toLocaleString()} done · <span data-testid="ai-desc-remaining">{s.remaining.toLocaleString()}</span> remaining.</p>
        </div>
        {s.running
          ? <button onClick={stop} className="rounded-full border border-border px-4 py-2 text-sm font-semibold" data-testid="ai-desc-stop-btn">Stop</button>
          : <button onClick={start} disabled={!s.remaining} className="inline-flex items-center gap-2 bg-emerald text-white rounded-full px-4 py-2 text-sm font-bold disabled:opacity-50" data-testid="ai-desc-start-btn"><Play size={14} /> {s.remaining ? "Generate all" : "All done"}</button>}
      </div>
      {(s.running || s.processed > 0) && (
        <div className="mt-4">
          <div className="flex justify-between text-xs font-semibold mb-1"><span>{s.running ? "Running…" : "Finished"} · {s.processed}/{s.total}</span><span>{s.updated} written · {s.failed} failed</span></div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-emerald transition-all duration-500" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
    </div>
  );
}


function AICommandCenter() {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [aiState, setAiState] = useState("idle");
  const [digest, setDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [command, setCommand] = useState("");
  const [cmdBusy, setCmdBusy] = useState(false);
  const [cmdResult, setCmdResult] = useState("");
  const runCommand = async () => {
    if (!command.trim()) return;
    setCmdBusy(true); setCmdResult("");
    try {
      const { data } = await api.post("/ai/command", { instruction: command });
      setCmdResult(data.msg || data.summary || "Done");
      if (data.ok) { toast.success("Shop Now AI did it"); setCommand(""); load(); }
      else toast.error(data.msg || "Couldn't do that");
    } catch (e) { toast.error(e?.response?.data?.detail || "Command failed"); }
    setCmdBusy(false);
  };

  const load = () => api.get("/ai/command-center").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); api.get("/ai/digest/latest").then(({ data }) => setDigest(data)).catch(() => {}); }, []);

  const genDigest = async () => {
    setDigestBusy(true);
    try { const { data } = await api.post("/ai/digest/generate"); setDigest(data); toast.success("Digest updated"); }
    catch { toast.error("Could not generate digest"); }
    setDigestBusy(false);
  };

  const runScan = async () => {
    setScanning(true); setAiState("thinking");
    try {
      const { data: r } = await api.post("/ai/scan");
      setAiState("success");
      toast.success(`Scan complete · ${r.auto_executed} auto-fixed · ${r.new_recommendations} new recommendation(s)`);
      await load();
    } catch { setAiState("error"); toast.error("Scan failed"); }
    setScanning(false);
    setTimeout(() => setAiState("idle"), 2200);
  };
  const approve = async (id) => { setBusyId(id); try { await api.post(`/ai/recommendations/${id}/approve`); toast.success("Approved & executed"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not execute"); } setBusyId(null); };
  const dismiss = async (id) => { setBusyId(id); try { await api.post(`/ai/recommendations/${id}/dismiss`); await load(); } catch { toast.error("Failed"); } setBusyId(null); };
  const rollback = async (id) => { setBusyId(id); try { await api.post(`/ai/ledger/${id}/rollback`); toast.success("Rolled back"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Rollback failed"); } setBusyId(null); };
  const setAutonomy = async (enabled) => { try { await api.put("/ai/autonomy", { autonomy_enabled: enabled, max_auto_risk: 1 }); toast.success(enabled ? "Autonomy ON — safe fixes run on their own" : "Autonomy paused"); await load(); } catch { toast.error("Failed"); } };

  if (!data) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={26} /></div>;
  const h = data.health, st = data.status;

  return (
    <div className="space-y-6" data-testid="ai-command-center">
      <AIOpsDashboard />
      <DescriptionJobPanel />
      <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-5 text-white">
        <div className="flex items-center gap-4 relative flex-wrap">
          <span className="h-14 w-14 rounded-2xl bg-white grid place-items-center shrink-0"><ShopNowAIMascot state={aiState} size={44} /></span>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg flex items-center gap-2">Shop Now AI <span className="text-[11px] font-semibold bg-emerald text-white rounded-full px-2 py-0.5">● Online</span></h3>
            <p className="text-white/60 text-xs">Engineering mode · observe → reason → plan → act → verify. Safe, reversible fixes run autonomously; risky changes wait for your approval.</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <ProviderSwitch />
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer" title="When ON, Level-1 reversible fixes run on their own">
              <span>Autonomy</span>
              <button onClick={() => setAutonomy(!st.autonomy_enabled)} data-testid="autonomy-toggle"
                className={`relative h-6 w-11 rounded-full transition-colors ${st.autonomy_enabled ? "bg-emerald" : "bg-white/25"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${st.autonomy_enabled ? "left-5" : "left-0.5"}`} />
              </button>
            </label>
            <button onClick={runScan} disabled={scanning} className="inline-flex items-center gap-2 bg-[#D8232A] hover:bg-[#b91d23] rounded-full px-4 py-2 text-sm font-bold disabled:opacity-60" data-testid="ai-scan-btn">
              {scanning ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run AI Scan
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="ai-health">
        {[["Live visitors", h.live_visitors, Radio], ["Orders 24h", h.orders_24h, Package], ["Products", h.products, LayoutGrid], ["Low stock", h.low_stock, AlertTriangle], ["Open tickets", h.open_tickets, LifeBuoy], ["Incidents", h.open_incidents, HeartPulse]].map(([l, v, Ic]) => (
          <div key={l} className="bg-card rounded-2xl border border-border p-4"><Ic size={18} className="text-emerald mb-1.5" /><div className="font-display font-bold text-xl">{v}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>
        ))}
      </div>

      <div className="bg-card rounded-2xl border border-border p-6" data-testid="ai-command-box">
        <div className="flex items-center gap-2 mb-1"><Bot size={18} className="text-emerald" /><h3 className="font-display font-bold">Ask Shop Now AI to change the site</h3></div>
        <p className="text-xs text-muted-foreground mb-3">Plain English — e.g. "create a category called Pet Supplies", "add an announcement saying Free shipping this weekend", or "delete the category Yachts". Every action is logged in the ledger below.</p>
        <form onSubmit={(e) => { e.preventDefault(); runCommand(); }} className="flex gap-2">
          <input value={command} onChange={(e) => setCommand(e.target.value)} placeholder="Tell Shop Now AI what to change…" className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="ai-command-input" />
          <button type="submit" disabled={cmdBusy || !command.trim()} className="bg-[#D8232A] text-white rounded-xl px-4 py-2.5 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="ai-command-send">
            {cmdBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Do it
          </button>
        </form>
        {cmdResult && <div className="mt-3 text-sm bg-secondary/40 rounded-xl p-3" data-testid="ai-command-result">{cmdResult}</div>}
      </div>

      <div className="bg-card rounded-2xl border border-border p-6" data-testid="ai-digest">
        <div className="flex items-center gap-2 mb-1">
          <ScrollText size={18} className="text-emerald" /><h3 className="font-display font-bold">Daily Insight Digest</h3>
          <button onClick={genDigest} disabled={digestBusy} className="ml-auto inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid="ai-digest-generate-btn">
            {digestBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {digest?.text ? "Regenerate" : "Generate"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">A plain-English summary of what Shop Now AI did and recommends. Auto-generated daily at 6am.</p>
        {digest?.text ? (
          <div className="bg-secondary/40 rounded-xl p-4 text-sm whitespace-pre-line leading-relaxed" data-testid="ai-digest-text">{digest.text}
            <div className="text-[11px] text-muted-foreground mt-2 pt-2 border-t border-border">{digest.created_at ? new Date(digest.created_at).toLocaleString() : ""}</div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-3 text-center">No digest yet — tap Generate to create today's summary.</p>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border p-6" data-testid="ai-recommendations">
        <div className="flex items-center gap-2 mb-1"><Lightbulb size={18} className="text-emerald" /><h3 className="font-display font-bold">Recommendations</h3><span className="text-xs text-muted-foreground">({data.recommendations.length} pending)</span></div>
        <p className="text-xs text-muted-foreground mb-4">Each carries a risk level from the Change-Risk Engine. Level 0–1 can run autonomously; Level 2+ needs your approval.</p>
        {data.recommendations.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No pending recommendations. Run an AI Scan to look for opportunities.</p>
        ) : (
          <div className="space-y-3">
            {data.recommendations.map((r) => (
              <div key={r.id} className="border border-border rounded-xl p-4" data-testid={`rec-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${RISK_META[r.risk_level]?.c}`}>{RISK_META[r.risk_level]?.l}</span>
                  <span className="font-semibold text-sm">{r.objective}</span>
                  <span className="ml-auto text-[11px] text-muted-foreground">confidence {Math.round((r.confidence || 0) * 100)}%</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{r.detail}</p>
                <div className="flex gap-2">
                  <button onClick={() => approve(r.id)} disabled={busyId === r.id} className="inline-flex items-center gap-1.5 bg-emerald text-white rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid={`rec-approve-${r.id}`}>
                    {busyId === r.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve & run
                  </button>
                  <button onClick={() => dismiss(r.id)} disabled={busyId === r.id} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid={`rec-dismiss-${r.id}`}>
                    <X size={13} /> Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border p-6" data-testid="ai-ledger">
        <div className="flex items-center gap-2 mb-1"><ScrollText size={18} className="text-emerald" /><h3 className="font-display font-bold">AI Action Ledger & Change Timeline</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Every action Shop Now AI takes is logged and reversible. This is your complete audit trail.</p>
        {data.ledger.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No actions logged yet.</p>
        ) : (
          <div className="divide-y divide-border/60">
            {data.ledger.map((e) => (
              <div key={e.id} className="py-3 flex items-start gap-3" data-testid={`ledger-${e.id}`}>
                <span className={`mt-0.5 text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap ${STATUS_META[e.status] || "bg-secondary"}`}>{e.status}{e.auto ? " · auto" : ""}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{e.objective}</div>
                  <div className="text-[11px] text-muted-foreground">{RISK_META[e.risk_level]?.l} · {e.agent} · {e.result || e.detail}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{e.executed_at || e.created_at ? new Date(e.executed_at || e.created_at).toLocaleString() : ""}</div>
                </div>
                {e.status === "executed" && (
                  <button onClick={() => rollback(e.id)} disabled={busyId === e.id} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold shrink-0" data-testid={`ledger-rollback-${e.id}`}>
                    {busyId === e.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Roll back
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SmsStubCodes() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/auth/sms/pending").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  if (!d || d.mode !== "stub") return null;
  return (
    <div className="mb-4 rounded-2xl border border-gold bg-gold/10 p-4 text-xs" data-testid="sms-stub-codes">
      <div className="font-bold">SMS delivery not configured (STUB mode) — pending codes</div>
      <p className="text-muted-foreground mt-0.5">Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN and TWILIO_FROM_NUMBER to send real texts. Until then, read codes here to help a customer.</p>
      {d.rows.length === 0 ? <p className="mt-2">No pending codes.</p> : <ul className="mt-2 space-y-1">{d.rows.map((r, i) => <li key={i} className="font-mono" data-testid={`sms-stub-${i}`}>{r.phone} · {r.kind} · <b>{r.code}</b> · expires {new Date(r.expires_at).toLocaleTimeString()}</li>)}</ul>}
    </div>
  );
}


function SecurityLogPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/security-events?limit=200").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  const color = (s) => s === "critical" ? "text-coral" : s === "warning" ? "text-gold-deep" : "text-muted-foreground";
  if (!rows) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={26} /></div>;
  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden" data-testid="security-log-panel">
      <div className="p-5 border-b border-border">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Activity size={18} className="text-emerald" /> Security Events</h3>
        <p className="text-xs text-muted-foreground">Logins, new-device admin sign-ins, privilege &amp; payout changes, password resets and passkey events. Newest first.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="p-3">When</th><th className="p-3">Event</th><th className="p-3">User</th><th className="p-3">IP</th><th className="p-3">Detail</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No security events yet</td></tr> : rows.map((e, i) => (
              <tr key={i} className="border-b border-border/60" data-testid={`sec-event-${i}`}>
                <td className="p-3 whitespace-nowrap text-xs">{e.ts ? new Date(e.ts).toLocaleString() : "—"}</td>
                <td className={`p-3 font-semibold ${color(e.severity)}`}>{e.kind}</td>
                <td className="p-3 text-xs">{e.user_email || "—"}</td>
                <td className="p-3 text-xs">{e.ip || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground max-w-xs truncate">{e.detail || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "overview");
  useEffect(() => { const t = sp.get("tab"); if (t) setTab(t); }, [sp]);
  const [ov, setOv] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [offline, setOffline] = useState({ customer_name: "", name: "", price: "", qty: 1, parish: "Kingston" });
  const [tickets, setTickets] = useState([]);
  const [discEdits, setDiscEdits] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [restockEdits, setRestockEdits] = useState({});

  const restock = async (pid) => {
    const add = Math.max(0, Number(restockEdits[pid] ?? 0));
    if (!add) { toast.error("Enter a quantity to add"); return; }
    try {
      const { data } = await api.post(`/admin/products/${pid}/restock`, { add });
      toast.success(`Restocked — now ${data.stock} units`);
      setRestockEdits((e) => ({ ...e, [pid]: "" }));
      const { data: ovd } = await api.get("/admin/overview"); setOv(ovd);
    } catch { toast.error("Could not restock"); }
  };

  const saveDiscount = async (uid) => {
    const pct = Math.max(0, Math.min(100, Number(discEdits[uid] ?? 0)));
    try {
      await api.post(`/admin/users/${uid}/discount`, { pct });
      toast.success(`Discount set to ${pct}%`);
      const { data } = await api.get("/admin/users"); setUsers(data);
    } catch { toast.error("Could not set discount"); }
  };

  const toggleBusiness = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/business`, { is_business: !u.is_business });
      toast.success(!u.is_business ? "Trade pricing enabled" : "Trade pricing removed");
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, is_business: !u.is_business } : x)));
    } catch { toast.error("Could not update business buyer"); }
  };

  useEffect(() => { if (user === false) navigate("/login"); else if (user && !["admin", "founder", "accountant", "auditor", "support"].includes(user.role)) navigate("/account"); }, [user]);
  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/overview").then(({ data }) => setOv(data)).catch(() => {});
      api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
      api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
      api.get("/admin/tickets").then(({ data }) => setTickets(data)).catch(() => {});
      api.get("/admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    } else if (user && ["accountant", "auditor", "support"].includes(user.role)) {
      setOv({ low_stock_count: 0, restricted: true });
      if (user.role === "support") api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (tab === "traffic" && user?.role === "admin") {
      const id = setInterval(() => api.get("/admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {}), 10000);
      return () => clearInterval(id);
    }
  }, [tab, user]);

  const resolveTicket = async (ref, payload) => {
    try {
      await api.post(`/admin/tickets/${ref}/resolve`, payload);
      toast.success("Case updated");
      const { data } = await api.get("/admin/tickets"); setTickets(data);
      api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    } catch { toast.error("Failed to update case"); }
  };

  const createOffline = async () => {
    if (!offline.customer_name || !offline.name || !offline.price) { toast.error("Fill customer, product and price"); return; }
    try {
      await api.post("/admin/offline-order", { customer_name: offline.customer_name, parish: offline.parish, items: [{ product_id: "manual", name: offline.name, price: Number(offline.price), quantity: Number(offline.qty) }] });
      toast.success("Offline order created");
      setOffline({ customer_name: "", name: "", price: "", qty: 1, parish: "Kingston" });
      api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    } catch { toast.error("Failed"); }
  };

  if (!ov) return <div className="grid place-items-center py-40"><Loader2 className="animate-spin text-emerald" size={32} /></div>;

  if (user && !user.mfa_enabled) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12" data-testid="admin-mfa-gate">
        <div className="bg-card rounded-2xl border border-border p-6">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><ShieldCheck className="text-emerald" /> Secure your admin account</h1>
          <p className="text-sm text-muted-foreground mt-1">Two-factor authentication is required for every admin. Set it up now to unlock the control center — a stolen password alone won't be enough to get in.</p>
          <MfaPanel onEnabled={refresh} />
        </div>
      </div>
    );
  }

  const ROLE_TABS = { accountant: ["master", "alerts", "accountant", "auditor", "haulage", "ai-gov", "roles", "ops", "resilience", "assurance", "knowledge", "bi"], auditor: ["master", "alerts", "accountant", "auditor", "haulage", "ai-gov", "security-log", "roles", "ops", "resilience", "assurance", "knowledge", "bi"], support: ["orders", "enquiries", "disputes", "compliance"] };
  const ALL_TABS = [["master", "Master View", LayoutGrid], ["bi", "Founder BI", TrendingUp], ["alerts", "Alert Center", Bell], ["ops", "Platform Health", HeartPulse], ["enterprise", "Enterprise Monitor", Gauge], ["founder-ai", "Founder AI Center", Sparkles], ["resilience", "AI Resilience", Cpu], ["assurance", "Finance Assurance", ShieldCheck], ["knowledge", "Knowledge Base", FileText], ["compliance", "Compliance", Gavel], ["export", "Global Export", Globe], ["montecarlo", "Monte Carlo AI", Sparkles], ["roles", "Role Governance", UserCog], ["accountant", "Accountant Portal", DollarSign], ["command", "Command Center", ShieldCheck], ["inventory", "Inventory Advisor", Package], ["auditor", "Auditor Portal", ShieldCheck], ["overview", "Overview", LayoutGrid], ["travel", "Travel & Stays", LayoutGrid], ["calc", "Pallet Loader", Container], ["materials", "Materials Catalogue", LayoutGrid], ["vehicles", "Vehicles", Container], ["haulage", "Haulage Control", Container], ["ai-gov", "AI Governance", ShieldCheck], ["imports", "Imports & Sourcing", Inbox], ["search", "Search Analytics", Activity], ["languages", "Languages", Globe], ["weather", "Weather", Activity], ["ai-command", "AI Command Center", Cpu], ["content", "Site Content", FileText], ["privacy", "Privacy & Data", ShieldCheck], ["ai-analyst", "AI Analyst", Sparkles], ["platform", "Platform", Server], ["traffic", "Traffic", Activity], ["orders", "Orders", Package], ["catalogue", "Catalogue", ClipboardList], ["quotes", "Quotes", Container], ["documents", "Documents", FileText], ["enquiries", "Enquiries", Sailboat], ["weekly-email", "Weekly Email", Mail], ["pay-links", "Pay Links", DollarSign], ["payouts", "Payouts & Security", ShieldCheck], ["security-log", "Security Log", Activity], ["price-log", "Price Log", History], ["disputes", "Dispute Center", Gavel], ["trust", "Trust & Safety", AlertTriangle], ["users", "Users", Users], ["offline", "Offline Order", PhoneCall]];
  const BASE_TABS = ROLE_TABS[user?.role] ? ALL_TABS.filter(([k]) => ROLE_TABS[user.role].includes(k)) : ALL_TABS;
  const TABS = user?.supplier_vault_access ? [BASE_TABS[0], ["vault", "Supplier Vault", Lock], ...BASE_TABS.slice(1)] : BASE_TABS;
  if (!TABS.some(([k]) => k === tab)) setTimeout(() => setTab(TABS[0][0]), 0);

  const refundOrder = async (o) => {
    const input = window.prompt(`Refund amount J$ for ${o.order_no} (leave blank for full ${o.totals.total})`, "");
    if (input === null) return;
    const amt = input.trim() ? Number(input) : null;
    try {
      const { data } = await api.post(`/admin/orders/${o.order_no}/refund`, { amount_jmd: amt, reason: "Admin refund" });
      toast.success(`Refund ${data.refund_status} \u00b7 ${data.method === "stripe" ? "Stripe" : "store credit"} J$${data.amount_jmd}`);
      api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  const markPaid = async (o) => {
    if (!window.confirm(`Confirm you've received payment for ${o.order_no}? This ships the order.`)) return;
    try {
      await api.post(`/admin/orders/${o.order_no}/mark-paid`);
      toast.success(`${o.order_no} marked paid`);
      api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark paid"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6"><h1 className="font-display text-3xl font-bold">Admin Control Center</h1><p className="text-muted-foreground">Platform-wide operations, merchants and logistics.</p></div>

      <div className="flex gap-2 mb-6 overflow-x-auto sn-scroll-x" data-testid="dashboard-nav-tabs">
        {TABS.map(([k, l, Ic]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold shrink-0 transition-colors ${tab === k ? "bg-ink text-white" : "bg-card border border-border"}`} data-testid={`tab-${k}`}>
            <Ic size={16} /> {l}
            {k === "overview" && ov.low_stock_count > 0 && <span className="ml-1 text-[10px] font-bold bg-coral text-white rounded-full px-1.5 py-0.5">{ov.low_stock_count}</span>}
          </button>
        ))}
      </div>

      {tab === "master" && <AdminMasterView />}
      {tab === "alerts" && <div className="space-y-4"><AlertCenter /><DailyBriefPanel /></div>}
      {tab === "accountant" && <AccountingLevels role={user?.role === "founder" ? "admin" : user?.role} />}
      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["GMV", jmd(ov.gmv), DollarSign], ["Orders", ov.orders, ShoppingCart], ["Users", ov.users, Users], ["Merchants", ov.merchants, Store], ["Products", ov.products, Package], ["Bookings", ov.bookings, Hotel], ["Open Tickets", ov.open_tickets, LifeBuoy], ["Warehouses", 3, LayoutGrid]].map(([l, v, Ic]) => (
              <div key={l} className="bg-card rounded-2xl border border-border p-5"><Ic size={20} className="text-emerald mb-2" /><div className="font-display font-bold text-2xl">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>
            ))}
          </div>
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-display font-bold mb-4">Gross Merchandise Value (J$ millions)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={GMV_DATA}><defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E11B22" stopOpacity={0.4} /><stop offset="100%" stopColor="#E11B22" stopOpacity={0} /></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" /><XAxis dataKey="m" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Area type="monotone" dataKey="v" stroke="#E11B22" strokeWidth={3} fill="url(#g)" /></AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6" data-testid="low-stock-panel">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={18} className={ov.low_stock_count > 0 ? "text-coral" : "text-emerald"} />
              <h3 className="font-display font-bold">Low Stock Alerts</h3>
              {ov.low_stock_count > 0 && <span className="ml-1 text-xs font-bold bg-coral text-white rounded-full px-2 py-0.5" data-testid="low-stock-count">{ov.low_stock_count}</span>}
            </div>
            <p className="text-xs text-muted-foreground mb-4">Products at or below 10 units — restock soon.</p>
            {(ov.low_stock || []).length === 0 ? (
              <p className="text-sm text-emerald font-medium">All products are well stocked. 👍</p>
            ) : (
              <div className="divide-y divide-border/60" data-testid="low-stock-list">
                {ov.low_stock.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-2.5">
                    <img src={p.image} alt={p.name} className="h-10 w-10 rounded-lg object-cover bg-secondary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.category}{p.sku ? ` · ${p.sku}` : ""}</div>
                    </div>
                    <span className={`text-sm font-bold shrink-0 px-2.5 py-1 rounded-lg ${p.stock === 0 ? "bg-coral text-white" : p.stock <= 5 ? "bg-coral/15 text-coral" : "bg-secondary text-ink"}`}>
                      {p.stock === 0 ? "Out of stock" : `${p.stock} left`}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <input type="number" min="1" placeholder="Qty" value={restockEdits[p.id] ?? ""}
                        onChange={(e) => setRestockEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && restock(p.id)}
                        className="w-16 text-sm rounded-lg border border-border px-2 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-coral/40"
                        data-testid={`restock-input-${p.id}`} />
                      <button onClick={() => restock(p.id)} data-testid={`restock-btn-${p.id}`}
                        className="flex items-center gap-1 text-sm font-semibold bg-ink text-white rounded-lg px-3 py-1.5 hover:bg-coral transition-colors">
                        <Plus size={14} /> Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-card rounded-2xl border border-border p-6" data-testid="recent-orders-panel">
            <h3 className="font-display font-bold mb-4">Recent Orders</h3>
            {(ov.recent_orders || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No orders yet.</p>
            ) : (
              <div className="divide-y divide-border/60">
                {ov.recent_orders.map((o) => (
                  <div key={o.id || o.order_no} className="flex items-center gap-3 py-2.5 text-sm">
                    <span className="font-mono-sn text-ink shrink-0">{o.order_no}</span>
                    <span className="text-muted-foreground truncate flex-1">{o.address?.full_name || o.email || "Guest"} · {o.items?.length || 0} item(s)</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${o.status === "delivered" ? "bg-emerald/15 text-emerald" : o.status === "cancelled" ? "bg-coral/15 text-coral" : "bg-secondary text-ink"}`}>{(o.status || "").replace(/_/g, " ")}</span>
                    <span className="font-bold shrink-0">{jmd(o.totals?.total || 0)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "traffic" && (
        <div className="space-y-6" data-testid="traffic-panel">
          <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-6 text-white flex items-center gap-4 flex-wrap">
            <span className="h-12 w-12 rounded-xl bg-coral grid place-items-center relative">
              <Radio size={24} />
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald animate-ping" />
            </span>
            <div>
              <div className="text-white/60 text-sm">Visitors on site right now</div>
              <div className="font-display font-bold text-4xl" data-testid="live-visitors">{analytics?.live ?? 0}</div>
            </div>
            <span className="ml-auto text-xs text-white/50">Live · refreshes every 10s</span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Views Today", analytics?.today_views ?? 0, Eye], ["Views (7 days)", analytics?.week_views ?? 0, Activity],
              ["Total Visitors", analytics?.total_visitors ?? 0, Users], ["Total Page Views", analytics?.total_views ?? 0, LayoutGrid]].map(([l, v, Ic]) => (
              <div key={l} className="bg-card rounded-2xl border border-border p-5"><Ic size={20} className="text-emerald mb-2" /><div className="font-display font-bold text-2xl">{(v || 0).toLocaleString()}</div><div className="text-xs text-muted-foreground">{l}</div></div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-display font-bold mb-4">Page Views — Last 7 Days</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={analytics?.series || []}><defs><linearGradient id="tg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E11B22" stopOpacity={0.4} /><stop offset="100%" stopColor="#E11B22" stopOpacity={0} /></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" /><XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} allowDecimals={false} /><Tooltip /><Area type="monotone" dataKey="views" stroke="#E11B22" strokeWidth={3} fill="url(#tg)" /></AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-display font-bold mb-4">Most Visited Pages</h3>
              {(analytics?.top_pages || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No traffic recorded yet.</p>
              ) : (
                <div className="space-y-2.5" data-testid="top-pages">
                  {analytics.top_pages.map((p) => (
                    <div key={p.path} className="flex items-center justify-between text-sm border-b border-border/60 pb-2 last:border-0">
                      <span className="font-mono-sn text-ink">{p.path === "/" ? "/ (Home)" : p.path}</span>
                      <span className="font-semibold text-emerald">{p.count.toLocaleString()} views</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4" data-testid="conversion-view">
            <div className="flex items-center gap-2"><TrendingUp size={18} className="text-coral" /><h3 className="font-display font-bold text-lg">Sales vs Traffic — Conversion</h3></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[["Orders Today", (analytics?.orders_today ?? 0).toLocaleString()],
                ["Orders (7 days)", (analytics?.orders_week ?? 0).toLocaleString()],
                ["Conversion Today", `${analytics?.conv_today ?? 0}%`],
                ["Conversion (7 days)", `${analytics?.conv_week ?? 0}%`]].map(([l, v]) => (
                <div key={l} className="bg-card rounded-2xl border border-border p-5">
                  <div className="font-display font-bold text-2xl" data-testid={`conv-${l.replace(/\s+/g, '-').toLowerCase()}`}>{v}</div>
                  <div className="text-xs text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-display font-bold mb-1">Visitors vs Orders — Last 7 Days</h3>
              <p className="text-xs text-muted-foreground mb-4">How many page views turned into orders each day.</p>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={analytics?.series || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" />
                  <XAxis dataKey="day" fontSize={12} />
                  <YAxis yAxisId="l" fontSize={12} allowDecimals={false} />
                  <YAxis yAxisId="r" orientation="right" fontSize={12} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Bar yAxisId="l" dataKey="views" name="Page Views" fill="#0f1115" radius={[4, 4, 0, 0]} barSize={22} />
                  <Line yAxisId="r" type="monotone" dataKey="orders" name="Orders" stroke="#E11B22" strokeWidth={3} dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-card rounded-2xl border border-border overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-secondary/60 text-left"><tr><th className="p-4">Order</th><th className="p-4">Items</th><th className="p-4">Total</th><th className="p-4">Payment</th><th className="p-4">Status</th><th className="p-4">Action</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-border"><td className="p-4 font-mono-sn">{o.order_no}{o.offline && <span className="ml-1 text-[10px] bg-gold/15 text-gold px-1.5 py-0.5 rounded">OFFLINE</span>}</td><td className="p-4">{o.items.length}</td><td className="p-4 font-semibold">{jmd(o.totals.total)}</td><td className="p-4 capitalize">{o.payment_status}{o.refund_status && <span className="ml-1 text-[10px] bg-coral/15 text-coral px-1.5 py-0.5 rounded capitalize">{o.refund_status.replace(/_/g, " ")}</span>}</td><td className="p-4"><span className="text-xs bg-emerald/10 text-emerald px-2 py-1 rounded-full capitalize">{o.status.replace(/_/g, " ")}</span></td><td className="p-4 flex flex-wrap gap-1.5">{o.payment_status === "awaiting_transfer" && <button onClick={() => markPaid(o)} className="text-xs font-semibold bg-emerald text-white rounded-lg px-3 py-1.5" data-testid={`mark-paid-btn-${o.order_no}`}>Mark paid</button>}{o.transfer_proof && <a href={assetUrl(o.transfer_proof)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-emerald underline px-2 py-1.5" data-testid={`proof-${o.order_no}`}>Receipt</a>}{o.refund_status === "refunded" ? <span className="text-xs text-muted-foreground">—</span> : <button onClick={() => refundOrder(o)} className="text-xs font-semibold border border-border rounded-lg px-3 py-1.5" data-testid={`refund-btn-${o.order_no}`}>Refund</button>}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "users" && (
        <div className="bg-card rounded-2xl border border-border overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-secondary/60 text-left"><tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Points</th><th className="p-4">Discount %</th><th className="p-4">Trade Buyer</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border"><td className="p-4 font-medium">{u.name}</td><td className="p-4 text-muted-foreground">{u.email}</td><td className="p-4"><span className="text-xs bg-ink text-white px-2 py-1 rounded-full capitalize">{u.role}</span></td><td className="p-4">{(u.points || 0).toLocaleString()}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <input type="number" min={0} max={100} defaultValue={u.discount_pct || 0}
                        onChange={(e) => setDiscEdits((d) => ({ ...d, [u.id]: e.target.value }))}
                        className="w-16 rounded-lg border border-border px-2 py-1.5 text-sm" data-testid="user-discount-input" />
                      <button onClick={() => saveDiscount(u.id)} className="bg-emerald text-white rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="user-discount-save">Set</button>
                    </div>
                  </td>
                  <td className="p-4">
                    <button onClick={() => toggleBusiness(u)} data-testid="user-business-toggle"
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${u.is_business ? "bg-ink text-white border-ink" : "border-border text-muted-foreground hover:border-ink"}`}>
                      {u.is_business ? "Trade pricing ON" : "Enable trade pricing"}
                    </button>
                  </td></tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground p-4">Set any customer's discount from 1% to 100%. It's applied automatically at their checkout. SHOP+ members get an automatic 5% if no manual discount is set.</p>
        </div>
      )}

      {tab === "offline" && (
        <div className="bg-card rounded-2xl border border-border p-6 max-w-2xl space-y-4">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><PhoneCall size={18} className="text-emerald" /> Create Offline / Phone Order</h3>
          <p className="text-sm text-muted-foreground">For corporate, phone and large-volume orders entered manually by SHOP NOW staff.</p>
          <input value={offline.customer_name} onChange={(e) => setOffline((o) => ({ ...o, customer_name: e.target.value }))} placeholder="Customer name" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="offline-customer" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input value={offline.name} onChange={(e) => setOffline((o) => ({ ...o, name: e.target.value }))} placeholder="Product / description" className="rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="offline-product" />
            <select value={offline.parish} onChange={(e) => setOffline((o) => ({ ...o, parish: e.target.value }))} className="rounded-xl border border-border px-4 py-2.5 text-sm">{["Kingston", "Montego Bay", "Portmore", "Spanish Town", "Mandeville", "Ocho Rios", "Negril"].map((p) => <option key={p}>{p}</option>)}</select>
            <input value={offline.price} onChange={(e) => setOffline((o) => ({ ...o, price: e.target.value }))} placeholder="Unit price (J$)" type="number" className="rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="offline-price" />
            <input value={offline.qty} onChange={(e) => setOffline((o) => ({ ...o, qty: e.target.value }))} placeholder="Quantity" type="number" className="rounded-xl border border-border px-4 py-2.5 text-sm" />
          </div>
          <button onClick={createOffline} className="bg-ink text-white rounded-xl px-6 py-2.5 font-semibold" data-testid="offline-create-btn">Create Order</button>
        </div>
      )}

      {tab === "ai-analyst" && <AIAnalyst />}
      {tab === "ai-command" && <AICommandCenter />}
      {tab === "travel" && <AdminTravelPanel />}
      {tab === "calc" && <AdminPalletPanel />}
      {tab === "materials" && <AdminMaterialsPanel />}
      {tab === "vehicles" && <AdminVehiclesPanel />}
      {tab === "haulage" && <AdminHaulagePanel />}
      {tab === "ai-gov" && <AdminAIGovernancePanel />}
      {tab === "roles" && <RoleGovernance />}
      {tab === "ops" && <OpsDashboard />}
      {tab === "resilience" && <ResiliencePanel />}
      {tab === "assurance" && <AssurancePanel />}
      {tab === "knowledge" && <KnowledgePanel />}
      {tab === "bi" && <FounderBI />}
      {tab === "imports" && <AdminImportQueue />}
      {tab === "search" && <div className="space-y-8"><CatalogueQA /><AdminSearchPanel /></div>}
      {tab === "command" && <CommandCenterPanel />}
      {tab === "inventory" && <InventoryAdvisor />}
      {tab === "auditor" && <AuditorPortal />}
      {tab === "content" && <SiteContentManager />}
      {tab === "privacy" && <PrivacyPanel />}
      {tab === "compliance" && <CompliancePanel />}
      {tab === "export" && <ExportPanel />}
      {tab === "montecarlo" && <MonteCarloCenter />}
      {tab === "vault" && <SupplierVault />}

      {tab === "languages" && <LanguageDashboard />}
      {tab === "weather" && <WeatherDashboard />}
      {tab === "enterprise" && <EnterpriseDashboard />}
      {tab === "founder-ai" && <FounderAICenter />}
      {tab === "platform" && <><ReleasePanel />{user?.is_founder && <CatalogueSync />}<PlatformOps /></>}

      {tab === "trust" && <TrustCenter />}

      {tab === "catalogue" && <div className="space-y-4"><CatalogueAudit /><DealApproval /><CatalogueManager /></div>}

      {tab === "enquiries" && <EnquiriesPanel />}

      {tab === "quotes" && <QuotesPanel />}

      {tab === "documents" && <DocumentsPanel />}
      {tab === "weekly-email" && (<><WeeklyEmailPanel /><CartRecoveryPanel /></>)}
      {tab === "pay-links" && <FygaroPayLinkPanel />}
      {tab === "payouts" && <PayoutsSecurityPanel />}
      {tab === "security-log" && <><SmsStubCodes /><SecurityLogPanel /></>}
      {tab === "price-log" && <AdminPriceLog />}

      {tab === "disputes" && (
        <div className="space-y-4" data-testid="dispute-center">
          <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-6 text-white">
            <div className="flex items-center gap-3 relative">
              <span className="h-11 w-11 rounded-xl bg-coral grid place-items-center"><Gavel size={22} /></span>
              <div><h3 className="font-display text-xl font-bold">Dispute & Refund Center</h3><p className="text-white/60 text-sm">Resolve cases filed by customers and SHOP AI.</p></div>
              <span className="ml-auto font-display font-bold text-2xl">{tickets.filter((t) => t.status === "open").length} <span className="text-sm font-normal text-white/60">open</span></span>
            </div>
          </div>
          {tickets.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No cases yet.</div>
          ) : tickets.map((t) => (
            <div key={t.ref} className="bg-card rounded-2xl border border-border p-5" data-testid="dispute-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-sn font-semibold">{t.ref}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${t.category === "return" ? "bg-coral/15 text-coral" : t.category === "complaint" ? "bg-gold/15 text-gold" : "bg-secondary text-muted-foreground"}`}>{t.category}</span>
                    {t.source === "shop_ai" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald/15 text-emerald">via SHOP AI</span>}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${t.status === "open" ? "bg-ink text-white" : "bg-emerald text-white"}`}>{t.status}</span>
                  </div>
                  <div className="font-semibold mt-1.5">{t.subject}</div>
                  {t.message && <p className="text-sm text-muted-foreground mt-0.5">{t.message}</p>}
                  <div className="text-xs text-muted-foreground mt-1">{t.order_no ? `Order ${t.order_no} · ` : ""}{t.email || "guest"}{t.resolution ? ` · Resolution: ${t.resolution}` : ""}</div>
                </div>
                {t.status === "open" && (
                  <div className="flex flex-wrap gap-2">
                    {t.category === "return" && t.order_no && (
                      <>
                        <button onClick={() => resolveTicket(t.ref, { status: "resolved", resolution: "Refund approved", refund_decision: "approved" })}
                          className="flex items-center gap-1.5 bg-emerald text-white rounded-lg px-3 py-2 text-sm font-semibold" data-testid="approve-refund-btn"><Check size={15} /> Approve Refund</button>
                        <button onClick={() => resolveTicket(t.ref, { status: "resolved", resolution: "Refund denied", refund_decision: "denied" })}
                          className="flex items-center gap-1.5 border border-border rounded-lg px-3 py-2 text-sm font-semibold" data-testid="deny-refund-btn"><X size={15} /> Deny</button>
                      </>
                    )}
                    <button onClick={() => resolveTicket(t.ref, { status: "resolved", resolution: "Resolved by admin" })}
                      className="flex items-center gap-1.5 bg-ink text-white rounded-lg px-3 py-2 text-sm font-semibold" data-testid="resolve-btn"><RotateCcw size={15} /> Mark Resolved</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
